
    <template>
      <section class="markdown-container theme-default-content">
      <head class="markdown"></head><body class="markdown"><h1 class="markdown">文件预览</h1>
<h2 id="介绍" class="markdown"><a class="header-anchor markdown" href="#介绍">#</a> 介绍</h2>
<p class="markdown">文件预览是为了解决 PC 端和移动端无法预览某些格式的文件，以及预览时格式错乱的问题，同时统一各类客户端的预览体验。</p>
<p class="markdown">在基础的预览功能上，我们还额外增加了水印、自定义页面标题等个性化功能。</p>
<h3 id="支持的文件格式" class="markdown"><a class="header-anchor markdown" href="#支持的文件格式">#</a> 支持的文件格式</h3>
<p class="markdown">支持预览的文件类型非常丰富，不仅有常见的文档格式，还包含音视频的媒体文件。</p>
<ul class="markdown">
<li class="markdown">文档：<code class="markdown">doc</code>、<code class="markdown">docx</code>、<code class="markdown">xls</code>、<code class="markdown">xlsx</code>、<code class="markdown">ppt</code>、<code class="markdown">pptx</code>、<code class="markdown">pdf</code>、<code class="markdown">txt</code>、<code class="markdown">markdown</code>、<code class="markdown">wps 文档格式</code></li>
<li class="markdown">图片：<code class="markdown">jpg</code>、<code class="markdown">png</code>、<code class="markdown">jpeg</code>、<code class="markdown">gif</code>、<code class="markdown">svg</code>、<code class="markdown">tiff</code>、<code class="markdown">jfif</code>、<code class="markdown">webp</code>、<code class="markdown">bmp</code></li>
<li class="markdown">音频：<code class="markdown">mp3</code>、<code class="markdown">wav</code>、<code class="markdown">m4a</code>、<code class="markdown">flac</code></li>
<li class="markdown">视频：<code class="markdown">mp4</code>、<code class="markdown">mov</code></li>
<li class="markdown">压缩包：<code class="markdown">rar</code>、<code class="markdown">rar-zip</code>、<code class="markdown">zip</code>、<code class="markdown">zip-rar</code>、<code class="markdown">7z</code></li>
</ul>
<p class="markdown">点击对应的按钮可以查看不同格式文件的预览效果。</p>
<demo-block hide-code="true" hide-border="true" class="markdown">
        
        <template slot="source" class="markdown"><element-demo0 class="markdown"></element-demo0></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 240px; font-size: 16px;"
        &gt;是否显示所有格式（总{{fileList.length}}种）&lt;/span
      &gt;
      &lt;el-switch v-model="showAll"&gt; &lt;/el-switch&gt;
    &lt;/div&gt;
    &lt;el-table
      :data="tableData"
      :span-method="objectSpanMethod"
      :cell-style="addCellBorder"
      border
    &gt;
      &lt;el-table-column prop="id" label="文件分类" width="150"&gt;
      &lt;/el-table-column&gt;
      &lt;el-table-column prop="type" label="文件格式" width="100"&gt;
      &lt;/el-table-column&gt;
      &lt;el-table-column prop="desc" label="说明"&gt; &lt;/el-table-column&gt;
      &lt;el-table-column prop="size" label="文件大小" width="100"&gt;
      &lt;/el-table-column&gt;
      &lt;el-table-column label="操作" width="80"&gt;
        &lt;template slot-scope="scope"&gt;
          &lt;cube-button
            @click.native.prevent="gotoPreview(scope.$index, tableData)"
            type="text"
            size="small"
          &gt;
            点击预览
          &lt;/cube-button&gt;
        &lt;/template&gt;
      &lt;/el-table-column&gt;
    &lt;/el-table&gt;
  &lt;/div&gt;
&lt;/template&gt;

&lt;script&gt;
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  export default {
    data() {
      return {
        showAll: false,
        // prettier-ignore
        fileList: [
          ["通用文档", ".pdf", "PDF 文件", "128K", "preview.pdf"],
          ["MS Office 文档", ".doc", "Word 97-2003 文件", "60K", "preview.doc"],
          ["MS Office 文档", ".docx", "Word 文件", "17K", "preview.docx"],
          ["MS Office 文档", ".dot", "Word 97-2003 模板文件", "112K", "preview.dot", 1],
          ["MS Office 文档", ".dotm", "Word 模板文件", "26K", "preview.dotm", 1],
          ["MS Office 文档", ".dotx", "Word 带宏的模板文件", "26K", "preview.dotx", 1],
          ["MS Office 文档", ".xls", "Excel 97-2003文件", "42K", "preview.xls"],
          ["MS Office 文档", ".xlsx", "Excel 文件", "24K", "preview.xlsx"],
          ["MS Office 文档", ".xla", "Excel 97-2003 加载宏文件", "34K", "preview.xla", 1],
          // ["MS Office 文档", ".xlam", "Excel 加载宏文件", "22K", "preview.xlam", 1],
          ["MS Office 文档", ".xlt", "Excel 97-2003 模板文件", "34K", "preview.xlt", 1],
          ["MS Office 文档", ".xltm", "Excel 启用宏的模板", "22K", "preview.xltm", 1],
          ["MS Office 文档", ".ppt", "PPT 97-2003 文件", "1.1M", "preview.ppt"],
          ["MS Office 文档", ".pptx", "PPT 文件", "187K", "preview.pptx"],

          ["WPS 文档", ".wps", "WPS文字 文件", "112K", "preview.wps"],
          ["WPS 文档", ".wpt", "WPS文字 模板文件", "112K", "preview.wpt", 1],
          ["WPS 文档", ".et", "WPS表格 文件", "34K", "preview.et"],
          ["WPS 文档", ".ett", "WPS表格 模板文件", "34K", "preview.ett", 1],
          ["WPS 文档", ".dps", "WPS演示 文件", "890K", "preview.dps"],
          ["macOS iWork 文档", ".pages", "macOS Pages 文稿文件", "719K", "preview.pages"],
          // ["macOS iWork 文档", ".numbers", "macOS Numbers 表格文件", "823K", "preview.numbers"],
          // ["macOS iWork 文档", ".key", "macOS Keynote 讲演文件", "1.5K", "preview.key"],

          ["纯文本", ".txt", "文本文件", "28K", "preview.txt"],
          ["纯文本", ".md", "Markdown 文件", "28K", "preview.md"],
          ["邮件", ".eml", "EML 文件", "28K", "preview.eml"],
          ["图片", ".png", "PNG 文件", "8K", "preview.png"],
          ["图片", ".jpg", "JPG 文件", "403K", "preview.jpg"],
          ["图片", ".jpeg", "JPEG 文件", "403K", "preview.jpeg", 1],
          ["图片", ".svg", "SVG 矢量文件", "56K", "preview.svg"],
          ["图片", ".jfif", "JFIF 文件", "419K", "preview.jfif"],
          ["图片", ".webp", "WEBP 文件", "284K", "preview.webp"],
          ["图片", ".bmp", "BMP 文件", "1M", "preview.bmp", 1],
          ["图片", ".gif", "GIF 文件", "130K", "preview.gif"],
          // ["图片", ".tiff", "TIF 文件", "940K", "preview.tiff"], // 待修复
          ["音频", ".mp3", "MP3 文件", "929K", "preview.mp3"],
          ["音频", ".flac", "FLAC 无损音频文件", "4.9M", "preview.flac"],
          // ["音频", ".ape", "APE 无损音频文件", "929K", "preview.ape"], // 不支持
          ["音频", ".m4a", "Apple 无损音频文件", "983K", "preview.m4a", 1],
          ["音频", ".wav", "Windows 无损音频文件", "10.2M", "preview.wav", 1],
          ["视频", ".mp4", "MP4 文件", "14.8M", "preview.mp4"],
          ["视频", ".mov", "MOV 文件", "5.4M", "preview.mov"],
          // ["视频", ".mkv", "MKV 文件", "5.3M", "preview.mkv"],
          // ["视频", ".mpg", "MPG 文件", "41M", "preview.mpg"],
          // ["视频", ".wmv", "WMV 文件", "40.8M", "preview.wmv"],
          // ["视频", ".rm", "RM 文件", "923K", "preview.rm"],
          // ["视频", ".rmvb", "RMVB 文件", "943K", "preview.rmvb"],
          ["压缩包", ".zip", "ZIP 压缩文件", "16.5M", "preview.zip"],
          ["压缩包", ".zip", "ZIP 压缩文件（内部嵌套 RAR）", "14.9M", "preview.rar.zip", 1],
          ["压缩包", ".rar", "RAR 压缩文件", "14.9M", "preview.rar"],
          ["压缩包", ".rar", "RAR 压缩文件（内部嵌套 ZIP）", "16.5M", "preview.zip.rar", 1],
          ["压缩包", ".7z", "7z 压缩文件", "14.9M", "preview.7z"],
        ],
      };
    },
    computed: {
      tableData() {
        return this.fileList
          .map((x) =&gt; ({
            id: x[0],
            type: x[1],
            desc: x[2],
            size: x[3],
            name: x[4],
            hide: x[5],
          }))
          .filter((x) =&gt; (this.showAll ? true : !x.hide));
      },
    },
    methods: {
      gotoPreview(index, data) {
        console.log("preview", data[index]);
        const url = base64encode(
          `https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/${data[index].name}`
        );
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        window.open(`${previewDomain}/onlinePreview/?url=${url}`, "_blank");

        // 调试用
        // const options = {
        //   // hiddenDownload: true,
        //   // hiddenPrint: true,
        //   hiddenConvert: true,
        //   xlsxReadOnly: true,
        // };
        // const optionsEncode = encodeURIComponent(
        //   Object.keys(options)
        //     .reduce((p, c) =&gt; {
        //       return p + `${c}=${base64encode(options[c])}&amp;`;
        //     }, "")
        //     .replace(/&amp;$/, "")
        // );
        // // console.log("optionsEncode", optionsEncode);
        // window.open(
        //   `${previewDomain}/onlinePreview/?url=${url}&amp;options=${optionsEncode}`,
        //   "_blank"
        // );
      },
      addCellBorder(event) {
        // console.log("addCellBorder", event);
        // if (event.columnIndex === 0) {
        return {
          borderBottom: "1px solid #EBEEF5",
        };
        // }
      },
      objectSpanMethod({ row, column, rowIndex, columnIndex }) {
        if (columnIndex === 0) {
          const _row = this.flitterData(this.tableData).one[rowIndex];
          const _col = _row &gt; 0 ? 1 : 0;
          return {
            rowspan: _row,
            colspan: _col,
          };
        }
      },
      flitterData(arr) {
        let spanOneArr = [];
        let concatOne = 0;
        arr.forEach((item, index) =&gt; {
          if (index === 0) {
            spanOneArr.push(1);
          } else {
            // name 修改
            if (item.id === arr[index - 1].id) {
              spanOneArr[concatOne] += 1;
              spanOneArr.push(0);
            } else {
              spanOneArr.push(1);
              concatOne = index;
            }
          }
        });
        return {
          one: spanOneArr,
        };
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><h2 id="使用方法" class="markdown"><a class="header-anchor markdown" href="#使用方法">#</a> 使用方法</h2>
<p class="markdown">我们支持两种方式的文件预览：</p>
<ol class="markdown">
<li class="markdown">URL 拼接</li>
<li class="markdown">接口调用</li>
</ol>
<h3 id="一-url-拼接" class="markdown"><a class="header-anchor markdown" href="#一-url-拼接">#</a> 一、URL 拼接</h3>
<p class="markdown">URL 拼接方式适合已有线上地址的文件，通过简单的拼接可以快速获得一个预览地址。</p>
<p class="markdown"><img src="/images/ams/file-preview-url-append.png" alt="file-preview-url-append" class="markdown"></p>
<h4 id="在线试用" class="markdown"><a class="header-anchor markdown" href="#在线试用">#</a> 在线试用</h4>
<demo-block hide-code="true" hide-border="true" class="markdown">
        
        <template slot="source" class="markdown"><element-demo1 class="markdown"></element-demo1></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 80px; font-size: 16px;"&gt;原始文件&lt;/span&gt;
      &lt;el-input placeholder="请输入原始文件地址" v-model="url"&gt; &lt;/el-input&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 80px; font-size: 16px;"&gt;水印文字&lt;/span&gt;
      &lt;el-input placeholder="请输入水印文字" v-model="watermarkText"&gt;
      &lt;/el-input&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 80px; font-size: 16px;"&gt;水印样式&lt;/span&gt;
    &lt;/div&gt;
    &lt;div
      style="display: flex; align-items: center; margin-bottom: 16px; gap: 16px;"
    &gt;
      &lt;el-input
        placeholder="水印透明度: 0.5"
        v-model="watermarkAlpha"
      &gt;&lt;/el-input&gt;
      &lt;el-input
        placeholder="水印宽度: 100px"
        v-model="watermarkWidth"
      &gt;&lt;/el-input&gt;
      &lt;el-input
        placeholder="水印文字大小: 24px"
        v-model="watermarkFontSize"
      &gt;&lt;/el-input&gt;
      &lt;el-input
        placeholder="水印文字颜色: #1259E5"
        v-model="watermarkColor"
      &gt;&lt;/el-input&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 220px; font-size: 16px;"&gt;是否显示 PDF 下载按钮&lt;/span&gt;
      &lt;el-switch v-model="hiddenDownload"&gt; &lt;/el-switch&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 220px; font-size: 16px;"&gt;是否显示 PDF 打印按钮&lt;/span&gt;
      &lt;el-switch v-model="hiddenPrint"&gt; &lt;/el-switch&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 220px; font-size: 16px;"
        &gt;是否隐藏 PDF/JPG 切换按钮（仅限 Office 文档类）&lt;/span
      &gt;
      &lt;el-switch v-model="hiddenConvert"&gt; &lt;/el-switch&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 220px; font-size: 16px;"
        &gt;是否设置 xlsx 为编辑模式&lt;/span
      &gt;
      &lt;el-switch v-model="xlsxReadOnly"&gt; &lt;/el-switch&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;p style="min-width: 80px; font-size: 16px;"&gt;预览地址&lt;/p&gt;
      &lt;p&gt;
        &lt;a
          target="_blank"
          :href="previewUrl"
          style="color: #1259E5; font-size: 14px; word-wrap: break-word; word-break: break-all;"
          &gt;{{previewUrl}}&lt;/a
        &gt;
      &lt;/p&gt;
    &lt;/div&gt;
    &lt;cube-button
      style="margin: 0 12px 12px 0"
      type="primary"
      @click="gotoPreview"
      &gt;直接预览&lt;/cube-button
    &gt;
  &lt;/div&gt;
&lt;/template&gt;

&lt;script&gt;
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  export default {
    data() {
      return {
        url: "",
        watermarkText: "",
        watermarkAlpha: "",
        watermarkFontSize: "",
        watermarkColor: "",
        watermarkWidth: "",
        hiddenDownload: false,
        hiddenPrint: false,
        hiddenConvert: false,
        xlsxReadOnly: false,
      };
    },
    computed: {
      previewUrl() {
        const fileUrl = base64encode(this.url);

        const options = {
          watermarkText: this.watermarkText,
          watermarkAlpha: this.watermarkAlpha,
          watermarkFontSize: this.watermarkFontSize,
          watermarkColor: this.watermarkColor,
          watermarkWidth: this.watermarkWidth,
          hiddenDownload: !this.hiddenDownload,
          hiddenPrint: !this.hiddenPrint,
          hiddenConvert: this.hiddenConvert,
          xlsxReadOnly: !this.xlsxReadOnly,
        };
        // console.log("options", options);
        // const _options = _(options).cloneDeep().value();
        const _options = _.pickBy(
          _.cloneDeep(options),
          (ov) =&gt; _.isBoolean(ov) || (_.isString(ov) &amp;&amp; ov)
        );
        // console.log("_options", _options);
        // 拼接每个 option，最终进行 encodeURI

        const optionsEncode = encodeURIComponent(
          Object.keys(_options)
            .reduce((p, c) =&gt; {
              return p + `${c}=${base64encode(options[c])}&amp;`;
            }, "")
            .replace(/&amp;$/, "")
        );
        console.log("optionsEncode", optionsEncode);
        let result = "";
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        if (this.url &amp;&amp; optionsEncode) {
          result = `${previewDomain}/onlinePreview/?url=${fileUrl}&amp;options=${optionsEncode}`;
        } else if (this.url) {
          result = `${previewDomain}/onlinePreview/?url=${fileUrl}`;
        }
        return result;
      },
    },
    methods: {
      gotoPreview() {
        window.open(this.previewUrl, "_blank");
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><h4 id="逐步示范" class="markdown"><a class="header-anchor markdown" href="#逐步示范">#</a> 逐步示范</h4>
<ol class="markdown">
<li class="markdown">若原始文件地址为: <code class="markdown">https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf</code>，<a href="https://www.base64encode.org/" class="markdown">base64</a> 之后为</li>
</ol>
<div class="language- markdown"><pre v-pre="" class="markdown"><code class="markdown">aHR0cHM6Ly9mcm9udC1lbmQtaHVhd2VpLWNkbi5kZXZvcHMuY25kaW5mby5jb20vc3RhdGljL2RvY3MvZmVkL3ByZXZpZXcvR2l0X0NvbW1pdF9NZXNzYWdlX0NvbnZlbnRpb25zLnBkZg==
</code></pre>
</div><ol start="2" class="markdown">
<li class="markdown">添加两个参数，分别为水印“仅限测试使用”和标题“自定义标题”，参数为 <code class="markdown">watermarkText=测试水印</code> 和 <code class="markdown">title=自定义标题</code></li>
<li class="markdown">对参数的值进行 base64, 结果为 <code class="markdown">watermarkText=5rWL6K+V5rC05Y2w</code> 和 <code class="markdown">title=6Ieq5a6a5LmJ5qCH6aKY</code></li>
<li class="markdown">对所有参数进行 <a href="https://www.sojson.com/encodeurl.html" class="markdown">encodeURIComponent</a>，得到 <code class="markdown">watermarkText%3D5rWL6K%2BV5rC05Y2w%26title%3D6Ieq5a6a5LmJ5qCH6aKY</code></li>
<li class="markdown">将地址和参数拼接到 <code class="markdown">https://ams.cube.cndinfo.com/onlinePreview/?url={}&amp;options={}</code> 中，得到最终地址 <a href="https://ams.cube.cndinfo.com/onlinePreview?url=aHR0cHM6Ly9mcm9udC1lbmQtaHVhd2VpLWNkbi5kZXZvcHMuY25kaW5mby5jb20vc3RhdGljL2RvY3MvZmVkL3ByZXZpZXcvR2l0X0NvbW1pdF9NZXNzYWdlX0NvbnZlbnRpb25zLnBkZg==&amp;options=watermarkText%3D5rWL6K%2BV5rC05Y2w" class="markdown">https://ams.cube.cndinfo.com/onlinePreview?url=aHR0cHM6Ly9mcm9udC1lbmQtaHVhd2VpLWNkbi5kZXZvcHMuY25kaW5mby5jb20vc3RhdGljL2RvY3MvZmVkL3ByZXZpZXcvR2l0X0NvbW1pdF9NZXNzYWdlX0NvbnZlbnRpb25zLnBkZg==&amp;options=watermarkText%3D5rWL6K%2BV5rC05Y2w</a></li>
</ol>
<p class="markdown">预览效果如下:</p>
<p class="markdown"><img src="/images/ams/file-preview-url-append-demo.png" alt="file-preview-url-append-demo" class="markdown"></p>
<h4 id="拼接参数说明" class="markdown"><a class="header-anchor markdown" href="#拼接参数说明">#</a> 拼接参数说明</h4>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">是否必填</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown"><code class="markdown">url</code></td>
<td class="markdown">String</td>
<td class="markdown">是</td>
<td class="markdown">经过 base64 转码的 Url 地址</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">options</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">预览参数拼接，需经过 encodeURIComponent 转码</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">officePreviewType</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文档预览模式，默认值 <code class="markdown">pdf</code>, 可选值 <code class="markdown">image</code>。仅支持 <code class="markdown">doc</code>、<code class="markdown">docx</code>、<code class="markdown">ppt</code>、<code class="markdown">pptx</code>、<code class="markdown">pdf</code> 格式</td>
</tr>
</tbody>
</table></div><h5 id="options-参数说明" class="markdown"><a class="header-anchor markdown" href="#options-参数说明">#</a> <code class="markdown">options</code> 参数说明</h5>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">是否必填</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown"><code class="markdown">watermarkText</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印，需经过 base64 转码</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">watermarkImg</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">图片水印 URL，需经过 base64 转码</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">watermarkAlpha</code></td>
<td class="markdown">String / Number</td>
<td class="markdown">否</td>
<td class="markdown">水印透明度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">watermarkFontSize</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体大小（单位 px）</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">watermarkColor</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体颜色</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">watermarkWidth</code></td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">水印宽度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">title</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">自定义标题（作用于 HTML 中的 <code class="markdown">title</code>、浏览器标签和微信分享卡片）</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">desc</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">自定义标题（作用于 HTML 中的 <code class="markdown">description</code>、微信分享卡片）</td>
</tr>
<tr class="markdown">
<td class="markdown"><code class="markdown">imgUrl</code></td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">微信分享卡片缩略图 URL</td>
</tr>
</tbody>
</table></div><p class="markdown"><strong class="markdown">注：<code class="markdown">watermarkText</code> 和 <code class="markdown">watermarkImg</code> 无法同时生效。另外由于微信的限制，<code class="markdown">imgUrl</code> 的域名必须和分享页面相同，其他域名图片无法生效。</strong></p>
<h4 id="示例代码及效果" class="markdown"><a class="header-anchor markdown" href="#示例代码及效果">#</a> 示例代码及效果</h4>
<h5 id="添加水印" class="markdown"><a class="header-anchor markdown" href="#添加水印">#</a> 添加水印</h5>
<demo-block class="markdown">
        <div class="markdown"><p class="markdown">文字水印</p>
</div>
        <template slot="source" class="markdown"><element-demo2 class="markdown"></element-demo2></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;cube-button type="primary" @click="gotoPreview"&gt;点击预览&lt;/cube-button&gt;
&lt;/template&gt;

&lt;script&gt;
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkText: "测试水印",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) =&gt; {
        return p + `${c}=${base64encode(options[c])}&amp;`;
      }, "")
      .replace(/&amp;$/, "")
  );

  export default {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview() {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = `${previewDomain}/onlinePreview/?url=${fileUrl}&amp;options=${optionsEncode}`;
        window.open(previewUrl, "_blank");
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><demo-block class="markdown">
        <div class="markdown"><p class="markdown">图片水印效果
<img src="/images/ams/file-preview-demo-watermark-image.png" alt="file-preview-demo-watermark-image" class="markdown"></p>
</div>
        <template slot="source" class="markdown"><element-demo3 class="markdown"></element-demo3></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;cube-button type="primary" @click="gotoPreview"&gt;点击预览&lt;/cube-button&gt;
&lt;/template&gt;

&lt;script&gt;
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkImg:
      "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/file-preview-watermark-image.png",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) =&gt; {
        return p + `${c}=${base64encode(options[c])}&amp;`;
      }, "")
      .replace(/&amp;$/, "")
  );

  export default {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview() {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = `${previewDomain}/onlinePreview/?url=${fileUrl}&amp;options=${optionsEncode}`;
        window.open(previewUrl, "_blank");
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><h5 id="自定义分享字段" class="markdown"><a class="header-anchor markdown" href="#自定义分享字段">#</a> 自定义分享字段</h5>
<demo-block class="markdown">
        <div class="markdown"><p class="markdown">自定义 <code class="markdown">title</code>, <code class="markdown">desc</code> 和 <code class="markdown">imgUrl</code> 效果。</p>
<p class="markdown"><img src="/images/ams/file-preview-demo-title-desc.png" alt="file-preview-demo-title-desc" class="markdown"></p>
<p class="markdown">点击预览后可转到至企微内查看效果。</p>
</div>
        <template slot="source" class="markdown"><element-demo4 class="markdown"></element-demo4></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;cube-button type="primary" @click="gotoPreview"&gt;点击预览&lt;/cube-button&gt;
&lt;/template&gt;

&lt;script&gt;
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkText: "测试水印",
    title: "自定义标题",
    desc: "自定义描述信息",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) =&gt; {
        return p + `${c}=${base64encode(options[c])}&amp;`;
      }, "")
      .replace(/&amp;$/, "")
  );

  export default {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview() {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = `${previewDomain}/onlinePreview/?url=${fileUrl}&amp;options=${optionsEncode}`;
        window.open(previewUrl, "_blank");
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><h5 id="pdf-和图片预览模式" class="markdown"><a class="header-anchor markdown" href="#pdf-和图片预览模式">#</a> PDF 和图片预览模式</h5>
<demo-block class="markdown">
        <div class="markdown"><p class="markdown"><code class="markdown">doc</code>、<code class="markdown">docx</code>、<code class="markdown">ppt</code>、<code class="markdown">pptx</code>、<code class="markdown">pdf</code> 格式支持两种预览模式，图片模式和 PDF 模式。</p>
<p class="markdown">默认模式为 PDF，预览速度较快。</p>
<p class="markdown">图片模式涉及到转换过程，速度稍慢，而且无法复制文字。</p>
<p class="markdown">建议用 PDF 模式，如果 PDF 下遇到排版问题，可尝试用图片模式看能否解决问题。</p>
</div>
        <template slot="source" class="markdown"><element-demo5 class="markdown"></element-demo5></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;cube-button type="primary" @click="gotoPreview('pdf')"
    &gt;查看PDF格式&lt;/cube-button
  &gt;
  &lt;cube-button type="primary" @click="gotoPreview('image')"
    &gt;查看图片格式&lt;/cube-button
  &gt;
&lt;/template&gt;

&lt;script&gt;
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkText: "测试水印",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) =&gt; {
        return p + `${c}=${base64encode(options[c])}&amp;`;
      }, "")
      .replace(/&amp;$/, "")
  );

  export default {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview(officePreviewType) {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = (officePreviewType) =&gt;
          `${previewDomain}/onlinePreview/?url=${fileUrl}&amp;options=${optionsEncode}&amp;officePreviewType=${officePreviewType}`;
        window.open(previewUrl(officePreviewType), "_blank");
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><h5 id="隐藏下载和打印按钮" class="markdown"><a class="header-anchor markdown" href="#隐藏下载和打印按钮">#</a> 隐藏下载和打印按钮</h5>
<demo-block class="markdown">
        <div class="markdown"><p class="markdown"><code class="markdown">doc</code>、<code class="markdown">docx</code>、<code class="markdown">ppt</code>、<code class="markdown">pptx</code>、<code class="markdown">pdf</code> 等文档格式在预览页面上支持打印和下载原文件。</p>
<p class="markdown"><img src="/images/ams/file-preview-download-button.png" alt="file-preview-download-button" class="markdown"></p>
<p class="markdown">但是这两个按钮默认是隐藏的。如果需要可以通过 <code class="markdown">hiddenPrint</code> 和 <code class="markdown">hiddenDownload</code> 参数显示按钮。</p>
</div>
        <template slot="source" class="markdown"><element-demo6 class="markdown"></element-demo6></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;cube-button type="primary" @click="gotoPreview('hide')"
    &gt;隐藏下载和打印按钮&lt;/cube-button
  &gt;
  &lt;cube-button type="primary" @click="gotoPreview()"&gt;不隐藏按钮&lt;/cube-button&gt;
&lt;/template&gt;

&lt;script&gt;
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  export default {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview(officePreviewType) {
        const options = {
          watermarkText: "测试水印",
          hiddenDownload: officePreviewType === "hide" ? true : false,
          hiddenPrint: officePreviewType === "hide" ? true : false,
        };
        // 拼接每个 option，最终进行 encodeURI
        const optionsEncode = encodeURIComponent(
          Object.keys(options)
            .reduce((p, c) =&gt; {
              return p + `${c}=${base64encode(options[c])}&amp;`;
            }, "")
            .replace(/&amp;$/, "")
        );
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = (officePreviewType) =&gt;
          `${previewDomain}/onlinePreview/?url=${fileUrl}&amp;options=${optionsEncode}&amp;officePreviewType=${officePreviewType}`;
        window.open(previewUrl(officePreviewType), "_blank");
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><h3 id="二-接口调用" class="markdown"><a class="header-anchor markdown" href="#二-接口调用">#</a> 二、接口调用</h3>
<p class="markdown">除了第一种拼接方式外，我们更推荐使用接口获取预览地址。这种方式更安全，同时可以控制预览次数和失效时间。</p>
<p class="markdown">具体接口参考<a href="/docs/ecosystem/ams/api" class="markdown">接口文档</a>。</p>
<h4 id="上传预览" class="markdown"><a class="header-anchor markdown" href="#上传预览">#</a> 上传预览</h4>
<p class="markdown">下面是上传文件进行预览的示例代码。</p>
<demo-block class="markdown">
        
        <template slot="source" class="markdown"><element-demo7 class="markdown"></element-demo7></template>
        <template slot="highlight" class="markdown"><pre v-pre="" class="markdown"><code class="html markdown">&lt;template&gt;
  &lt;div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 160px; font-size: 16px;"&gt;AMS appKey&lt;/span&gt;
      &lt;el-input placeholder="请输入 AMS appKey" v-model="appKey"&gt; &lt;/el-input&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 160px; font-size: 16px;"&gt;AMS appSecret&lt;/span&gt;
      &lt;el-input placeholder="请输入 AMS appSecret" v-model="appSecret"&gt;
      &lt;/el-input&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 160px; font-size: 16px;"&gt;水印文字&lt;/span&gt;
      &lt;el-input placeholder="请输入水印文字" v-model="watermarkText"&gt;
      &lt;/el-input&gt;
    &lt;/div&gt;
    &lt;div style="display: flex; align-items: center; margin-bottom: 16px;"&gt;
      &lt;span style="width: 160px; font-size: 16px;"&gt;访问次数限制&lt;/span&gt;
      &lt;el-input placeholder="请输入允许预览几次" v-model="count"&gt; &lt;/el-input&gt;
    &lt;/div&gt;
    &lt;el-upload
      class="upload"
      ref="upload"
      action="string"
      :file-list="fileList"
      :auto-upload="false"
      :on-change="handleChange"
      :on-preview="handlePreview"
      :on-remove="handleRemove"
      multiple="multiple"
    &gt;
      &lt;cube-button slot="trigger" size="medium" type="primary" @click="delFile"
        &gt;1. 选取文件&lt;/cube-button
      &gt;
    &lt;/el-upload&gt;
    &lt;cube-button
      style="margin: 12px 12px 12px 0"
      size="medium"
      type="primary"
      @click="submitUpload"
      &gt;2. 点击预览&lt;/cube-button
    &gt;
  &lt;/div&gt;
&lt;/template&gt;

&lt;script&gt;
  import axios from "axios";
  import CryptoJS from "crypto-js/crypto-js";
  const base64encode = (str) =&gt;
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  export default {
    data() {
      return {
        appKey: "",
        appSecret: "",
        watermarkText: "",
        count: "",
        fileList: [],
        multiple: false,
      };
    },
    methods: {
      delFile() {
        console.log("删除文件");
        this.fileList = [];
      },
      handleChange(file, fileList) {
        this.fileList = fileList;
      },
      handleRemove(file, fileList) {
        console.log(file, fileList);
      },
      handlePreview(file) {
        console.log(file);
      },
      submitUpload() {
        let formData = new FormData();
        if (this.fileList[0]) {
          formData.append("previewFile", this.fileList[0].raw);
        }
        if (this.watermarkText) {
          formData.append("watermarkText", this.watermarkText);
        }
        if (this.count) {
          formData.append("count", this.count);
        }

        const previewDomain = "/ams-api"; // "https://ams.cube.cndinfo.com/api"

        const timestamp = Date.now();
        const data = {
          appKey: this.appKey,
          timestamp,
          appSecret: this.appSecret,
          sign: CryptoJS.SHA256(
            this.appKey + timestamp + this.appSecret
          ).toString(),
        };

        // 演示代码通过前端获取 token，实际开发中应该由后端获取
        axios({
          url: previewDomain + "/auth/sign",
          method: "post",
          data,
          headers: {
            "Content-Type": "application/json;chaset=UTF-8",
          },
        })
          .then((res) =&gt; {
            console.log("AMS 鉴权: ", res);
            if (res.data &amp;&amp; res.data.data &amp;&amp; res.data.data.accessToken) {
              axios({
                url: previewDomain + "/preview/secure/file/onlinePreview",
                method: "post",
                data: formData,
                headers: {
                  "Content-Type": "multipart/form-data;charset=utf-8",
                  Authorization: `Bearer ${res.data.data.accessToken}`,
                },
              })
                .then((res2) =&gt; {
                  console.log("文件预览: ", res2);
                  if (
                    res2.data &amp;&amp;
                    res2.data.data &amp;&amp;
                    res2.data.data.previewUrl
                  ) {
                    window.open(res2.data.data.previewUrl, "_blank");
                  } else {
                  }
                })
                .catch((err) =&gt; {
                  console.log(err);
                });
            } else {
            }
          })
          .catch((err) =&gt; {
            console.log(err);
          });
      },
    },
  };
&lt;/script&gt;
</code></pre></template></demo-block><h2 id="pc-web-用法" class="markdown"><a class="header-anchor markdown" href="#pc-web-用法">#</a> PC Web 用法</h2>
<p class="markdown">PC 端参考上述的实例代码，或调用<a href="/docs/ecosystem/ams/api" class="markdown">原始的预览接口</a>进行预览。</p>
<h2 id="移动端用法" class="markdown"><a class="header-anchor markdown" href="#移动端用法">#</a> 移动端用法</h2>
<p class="markdown">拼接方式无法预览本地文件，同时也无法给预览设置时效。</p>
<p class="markdown">因此，我们建议大多数场景下都使用接口进行预览。</p>
<h3 id="安装-sdk" class="markdown"><a class="header-anchor markdown" href="#安装-sdk">#</a> 安装 SDK</h3>
<p class="markdown">接口调用需要使用 <a href="https://cube.cndinfo.com/docs/ecosystem/ams/sdk" class="markdown">ams-sdk</a>。</p>
<p class="markdown">SDK 只支持移动端。</p>
<div class="language-sh markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token comment markdown"># 安装 ams sdk</span>
<span class="token function markdown">npm</span> <span class="token function markdown">install</span> @cndinfo/cube-ams-sdk <span class="token parameter variable markdown">--save</span> <span class="token parameter variable markdown">--registry</span> https://npm.devops.cndinfo.com/
</code></pre>
</div><p class="markdown">初始化 SDK：</p>
<div class="language-js markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token comment markdown">// 建议创建单独的 `ams.js` 文件</span>
<span class="token keyword markdown">import</span> ams <span class="token keyword markdown">from</span> <span class="token string markdown">"@cndinfo/cube-ams-sdk"</span><span class="token punctuation markdown">;</span>

<span class="token keyword markdown">export</span> <span class="token keyword markdown">const</span> amsClient <span class="token operator markdown">=</span> <span class="token function markdown">ams</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
  <span class="token literal-property property markdown">appKey</span><span class="token operator markdown">:</span> <span class="token string markdown">"your appKey"</span><span class="token punctuation markdown">,</span> <span class="token comment markdown">// 必需</span>
  <span class="token literal-property property markdown">appSecret</span><span class="token operator markdown">:</span> <span class="token string markdown">"your appSecret"</span><span class="token punctuation markdown">,</span> <span class="token comment markdown">// 必需</span>
  <span class="token literal-property property markdown">hotPushId</span><span class="token operator markdown">:</span> <span class="token string markdown">"用于热更新的子应用编号"</span><span class="token punctuation markdown">,</span> <span class="token comment markdown">// 非必需</span>
  <span class="token comment markdown">// #ifdef  H5</span>
  <span class="token comment markdown">// H5通过代理方式解决跨域问题</span>
  <span class="token literal-property property markdown">apiUrl</span><span class="token operator markdown">:</span> <span class="token string markdown">"/ams-api"</span><span class="token punctuation markdown">,</span>
  <span class="token comment markdown">// #endif</span>

  <span class="token comment markdown">// #ifdef  APP-PLUS</span>
  <span class="token comment markdown">// APP 端无跨域问题，填写固定地址</span>
  <span class="token literal-property property markdown">apiUrl</span><span class="token operator markdown">:</span> <span class="token string markdown">"https://ams.cube.cndinfo.com/api"</span><span class="token punctuation markdown">,</span>
  <span class="token comment markdown">// #endif</span>
<span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span><span class="token punctuation markdown">;</span>
</code></pre>
</div><p class="markdown">在安装 SDK 之后，需要在 <a href="https://uniapp.dcloud.io/collocation/vue-config.html" class="markdown"><code class="markdown">vue.config.js</code></a> 中添加如下配置，解决跨域问题（如果根目录没有就自行创建一个）。</p>
<blockquote class="markdown">
<p class="markdown">如果在 <code class="markdown">manifest.json</code> 中已经配置过 <code class="markdown">devServer</code>，则继续在 <code class="markdown">manifest.json</code> 中添加。<br class="markdown">
因为 <code class="markdown">manifest.json</code> 中的 <code class="markdown">devServer</code> 配置会覆盖 <code class="markdown">vue.config.js</code>。</p>
</blockquote>
<div class="language-js markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token comment markdown">// vue.config.js</span>
<span class="token keyword markdown">const</span> <span class="token constant markdown">VUE_CUBE_AMS_API</span> <span class="token operator markdown">=</span> <span class="token string markdown">"/ams-api"</span><span class="token punctuation markdown">;</span>
module<span class="token punctuation markdown">.</span>exports <span class="token operator markdown">=</span> <span class="token punctuation markdown">{</span>
  <span class="token literal-property property markdown">devServer</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
    <span class="token literal-property property markdown">proxy</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
      <span class="token punctuation markdown">[</span><span class="token constant markdown">VUE_CUBE_AMS_API</span><span class="token punctuation markdown">]</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
        <span class="token literal-property property markdown">target</span><span class="token operator markdown">:</span> <span class="token template-string markdown"><span class="token template-punctuation string markdown">`</span><span class="token string markdown">https://ams.cube.cndinfo.com/api</span><span class="token template-punctuation string markdown">`</span></span><span class="token punctuation markdown">,</span>
        <span class="token literal-property property markdown">changeOrigin</span><span class="token operator markdown">:</span> <span class="token boolean markdown">true</span><span class="token punctuation markdown">,</span>
        <span class="token literal-property property markdown">pathRewrite</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
          <span class="token punctuation markdown">[</span><span class="token string markdown">"^"</span> <span class="token operator markdown">+</span> <span class="token constant markdown">VUE_CUBE_AMS_API</span><span class="token punctuation markdown">]</span><span class="token operator markdown">:</span> <span class="token string markdown">""</span><span class="token punctuation markdown">,</span>
        <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
  <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
<span class="token punctuation markdown">}</span><span class="token punctuation markdown">;</span>
</code></pre>
</div><h3 id="sdk-api" class="markdown"><a class="header-anchor markdown" href="#sdk-api">#</a> SDK API</h3>
<h4 id="previewfilebylink" class="markdown"><a class="header-anchor markdown" href="#previewfilebylink">#</a> previewFileByLink()</h4>
<p class="markdown">通过 url 生成预览地址。和拼接方式类似。</p>
<ul class="markdown">
<li class="markdown">方法参数</li>
</ul>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">是否必填</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown">previewUrl</td>
<td class="markdown">String</td>
<td class="markdown">是</td>
<td class="markdown">需要预览的文件链接</td>
</tr>
<tr class="markdown">
<td class="markdown">compress</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">压缩比例 0 - 1，数字越小压缩比例越高，默认为 1</td>
</tr>
<tr class="markdown">
<td class="markdown">expireTime</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">预览链接过期分钟数，默认为 5 分钟</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFile</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">否</td>
<td class="markdown">水印图片文件</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkImg</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印图片 URL</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkText</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印文字</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkAlpha</td>
<td class="markdown">String / Number</td>
<td class="markdown">否</td>
<td class="markdown">水印透明度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFontSize</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体大小（单位 px）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkColor</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体颜色</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkWidth</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">水印宽度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenPrint</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的打印按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenDownload</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的下载按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenConvert</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面的格式切换按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">xlsxReadOnly</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">设置 Excel 文件是否为只读模式</td>
</tr>
</tbody>
</table></div><ul class="markdown">
<li class="markdown">返回字段</li>
</ul>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown">previewUrl</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">预览链接</td>
</tr>
<tr class="markdown">
<td class="markdown">expireTime</td>
<td class="markdown">Number</td>
<td class="markdown">预览地址过期时间</td>
</tr>
</tbody>
</table></div><div class="language-json markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token punctuation markdown">{</span>
  <span class="token property markdown">"code"</span><span class="token operator markdown">:</span> <span class="token string markdown">"200"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"message"</span><span class="token operator markdown">:</span> <span class="token string markdown">"OK"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"data"</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
    <span class="token property markdown">"expireTime"</span><span class="token operator markdown">:</span> <span class="token number markdown">1664329090</span><span class="token punctuation markdown">,</span>
    <span class="token property markdown">"previewUrl"</span><span class="token operator markdown">:</span> <span class="token string markdown">"https://ams.cube.cndinfo.com/onlinePreview?url=aHR0cHM6Ly93d3cuYmFpZHUuY29tL2ltZy9mbGV4aWJsZS9sb2dvL3BjL3BlYWstcmVzdWx0LnBuZw%3D%3D&amp;options=watermarkText%3D5paH5a2X5rC05Y2w"</span>
  <span class="token punctuation markdown">}</span>
<span class="token punctuation markdown">}</span>
</code></pre>
</div><h4 id="previewfilebyupload" class="markdown"><a class="header-anchor markdown" href="#previewfilebyupload">#</a> previewFileByUpload()</h4>
<p class="markdown">上传本地文件生成预览地址。</p>
<ul class="markdown">
<li class="markdown">方法参数</li>
</ul>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">是否必填</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown">filePath</td>
<td class="markdown">文件 Blob URL</td>
<td class="markdown">和 <code class="markdown">fileObject</code> 必填一个</td>
<td class="markdown">需要预览的本地文件路径（<code class="markdown">uni.downloadFile</code> 下载的都为 Blob URL 格式）</td>
</tr>
<tr class="markdown">
<td class="markdown">fileObject</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">和 <code class="markdown">filePath</code> 必填一个</td>
<td class="markdown">需要预览的本地文件对象</td>
</tr>
<tr class="markdown">
<td class="markdown">compress</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">压缩比例 0 - 1，数字越小压缩比例越高，默认为 1</td>
</tr>
<tr class="markdown">
<td class="markdown">expireTime</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">预览链接过期分钟数，默认为 5 分钟</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFile</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">否</td>
<td class="markdown">水印图片</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkImg</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印图片 URL</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkText</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印文字</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkAlpha</td>
<td class="markdown">String / Number</td>
<td class="markdown">否</td>
<td class="markdown">水印透明度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFontSize</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体大小（单位 px）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkColor</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体颜色</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkWidth</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">水印宽度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">uploadMethod</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">上传文件调用的方法 (<code class="markdown">uni</code>, <code class="markdown">plus</code>)</td>
</tr>
<tr class="markdown">
<td class="markdown">options</td>
<td class="markdown">Object</td>
<td class="markdown">否</td>
<td class="markdown"><code class="markdown">plus.uploader.createUpload</code> 接收的额外参数</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenPrint</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的打印按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenDownload</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的下载按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenConvert</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面的格式切换按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">xlsxReadOnly</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">设置 Excel 文件是否为只读模式</td>
</tr>
</tbody>
</table></div><blockquote class="markdown">
<p class="markdown"><code class="markdown">uploadMethod</code> 有两个可选值:</p>
<ul class="markdown">
<li class="markdown"><code class="markdown">uni</code> (默认): 调用 <a href="https://uniapp.dcloud.net.cn/api/request/network-file.html#uploadfile" class="markdown">uni.uploadFile</a>，各客户端都适用</li>
<li class="markdown"><code class="markdown">plus</code>: 调用 <a href="https://www.dcloud.io/docs/api/zh_cn/uploader.html#plus.uploader.createUpload" class="markdown">plus.uploader.createUpload</a> 方法，通常适用于 App 内的 H5<br class="markdown">
App 端（App 和 App 内 H5）优先采用默认值 <code class="markdown">uni</code>，如果默认值上传失败，可切换至 <code class="markdown">plus</code>。</li>
</ul>
</blockquote>
<ul class="markdown">
<li class="markdown">返回字段</li>
</ul>
<div class="language-json markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token punctuation markdown">{</span>
  <span class="token property markdown">"code"</span><span class="token operator markdown">:</span> <span class="token string markdown">"200"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"message"</span><span class="token operator markdown">:</span> <span class="token string markdown">"OK"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"data"</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
    <span class="token property markdown">"expireTime"</span><span class="token operator markdown">:</span> <span class="token number markdown">1664329090</span><span class="token punctuation markdown">,</span>
    <span class="token property markdown">"previewUrl"</span><span class="token operator markdown">:</span> <span class="token string markdown">"https://ams.cube.cndinfo.com/onlinePreview?url=aHR0cHM6Ly93d3cuYmFpZHUuY29tL2ltZy9mbGV4aWJsZS9sb2dvL3BjL3BlYWstcmVzdWx0LnBuZw%3D%3D&amp;options=watermarkText%3D5paH5a2X5rC05Y2w"</span>
  <span class="token punctuation markdown">}</span>
<span class="token punctuation markdown">}</span>
</code></pre>
</div><h4 id="securepreviewbylink" class="markdown"><a class="header-anchor markdown" href="#securepreviewbylink">#</a> securePreviewByLink()</h4>
<p class="markdown">安全预览模式，需要 AMS 鉴权。支持预览次数限制。其余用法和 <code class="markdown">previewFileByLink</code> 一致。</p>
<ul class="markdown">
<li class="markdown">方法参数</li>
</ul>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">是否必填</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown">previewUrl</td>
<td class="markdown">String</td>
<td class="markdown">是</td>
<td class="markdown">需要预览的文件链接</td>
</tr>
<tr class="markdown">
<td class="markdown">compress</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">压缩比例 0 - 1，数字越小压缩比例越高，默认为 1</td>
</tr>
<tr class="markdown">
<td class="markdown">expireTime</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">预览链接过期分钟数，默认为 5 分钟</td>
</tr>
<tr class="markdown">
<td class="markdown">count</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">可预览次数，超过次数后无法打开（默认不限制次数）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFile</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">否</td>
<td class="markdown">水印图片文件</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkImg</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印图片 URL</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkText</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印文字</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkAlpha</td>
<td class="markdown">String / Number</td>
<td class="markdown">否</td>
<td class="markdown">水印透明度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFontSize</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体大小（单位 px）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkColor</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体颜色</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkWidth</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">水印宽度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenPrint</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的打印按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenDownload</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的下载按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenConvert</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面的格式切换按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">xlsxReadOnly</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">设置 Excel 文件是否为只读模式</td>
</tr>
</tbody>
</table></div><ul class="markdown">
<li class="markdown">返回字段</li>
</ul>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown">previewUrl</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">预览链接</td>
</tr>
<tr class="markdown">
<td class="markdown">expireTime</td>
<td class="markdown">Number</td>
<td class="markdown">预览地址过期时间</td>
</tr>
</tbody>
</table></div><div class="language-json markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token punctuation markdown">{</span>
  <span class="token property markdown">"code"</span><span class="token operator markdown">:</span> <span class="token string markdown">"200"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"message"</span><span class="token operator markdown">:</span> <span class="token string markdown">"OK"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"data"</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
    <span class="token property markdown">"expireTime"</span><span class="token operator markdown">:</span> <span class="token number markdown">1718328281</span><span class="token punctuation markdown">,</span>
    <span class="token property markdown">"previewUrl"</span><span class="token operator markdown">:</span> <span class="token string markdown">"https://ams.cube.cndinfo.com/onlinePreview/secure?token=jrjDSjmaETsS5MsSS10MmL2ZfhaKWMfb"</span>
  <span class="token punctuation markdown">}</span>
<span class="token punctuation markdown">}</span>
</code></pre>
</div><h4 id="securepreviewbyupload" class="markdown"><a class="header-anchor markdown" href="#securepreviewbyupload">#</a> securePreviewByUpload()</h4>
<p class="markdown">安全预览模式，需要 AMS 鉴权。支持预览次数限制。其余用法和 <code class="markdown">previewFileByUpload</code> 一致。</p>
<ul class="markdown">
<li class="markdown">方法参数</li>
</ul>
<div class="table markdown"><table class="markdown"><thead class="markdown">
<tr class="markdown">
<th class="markdown">参数</th>
<th class="markdown">类型</th>
<th class="markdown">是否必填</th>
<th class="markdown">说明</th>
</tr>
</thead>
<tbody class="markdown">
<tr class="markdown">
<td class="markdown">filePath</td>
<td class="markdown">文件 Blob URL</td>
<td class="markdown">和 <code class="markdown">fileObject</code> 必填一个</td>
<td class="markdown">需要预览的本地文件路径（<code class="markdown">uni.downloadFile</code> 下载的都为 Blob URL 格式）</td>
</tr>
<tr class="markdown">
<td class="markdown">fileObject</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">和 <code class="markdown">filePath</code> 必填一个</td>
<td class="markdown">需要预览的本地文件对象</td>
</tr>
<tr class="markdown">
<td class="markdown">compress</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">压缩比例 0 - 1，数字越小压缩比例越高，默认为 1</td>
</tr>
<tr class="markdown">
<td class="markdown">expireTime</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">预览链接过期分钟数，默认为 5 分钟</td>
</tr>
<tr class="markdown">
<td class="markdown">count</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">可预览次数，超过次数后无法打开（默认不限制次数）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFile</td>
<td class="markdown">File 对象实例</td>
<td class="markdown">否</td>
<td class="markdown">水印图片</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkImg</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印图片 URL</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkText</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">水印文字</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkAlpha</td>
<td class="markdown">String / Number</td>
<td class="markdown">否</td>
<td class="markdown">水印透明度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkFontSize</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体大小（单位 px）</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkColor</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">文字水印字体颜色</td>
</tr>
<tr class="markdown">
<td class="markdown">watermarkWidth</td>
<td class="markdown">Number</td>
<td class="markdown">否</td>
<td class="markdown">水印宽度（包含文字和图片）</td>
</tr>
<tr class="markdown">
<td class="markdown">uploadMethod</td>
<td class="markdown">String</td>
<td class="markdown">否</td>
<td class="markdown">上传文件调用的方法 (<code class="markdown">uni</code>, <code class="markdown">plus</code>)</td>
</tr>
<tr class="markdown">
<td class="markdown">options</td>
<td class="markdown">Object</td>
<td class="markdown">否</td>
<td class="markdown"><code class="markdown">plus.uploader.createUpload</code> 接收的额外参数</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenPrint</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的打印按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenDownload</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面右上角的下载按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">hiddenConvert</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">是否隐藏文档格式预览页面的格式切换按钮</td>
</tr>
<tr class="markdown">
<td class="markdown">xlsxReadOnly</td>
<td class="markdown">Boolean</td>
<td class="markdown">否</td>
<td class="markdown">设置 Excel 文件是否为只读模式</td>
</tr>
</tbody>
</table></div><p class="markdown"><code class="markdown">uploadMethod</code> 用法参考 <code class="markdown">previewFileByUpload</code> 方法。</p>
<ul class="markdown">
<li class="markdown">返回字段</li>
</ul>
<div class="language-json markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token punctuation markdown">{</span>
  <span class="token property markdown">"code"</span><span class="token operator markdown">:</span> <span class="token string markdown">"200"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"message"</span><span class="token operator markdown">:</span> <span class="token string markdown">"OK"</span><span class="token punctuation markdown">,</span>
  <span class="token property markdown">"data"</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
    <span class="token property markdown">"expireTime"</span><span class="token operator markdown">:</span> <span class="token number markdown">1718328281</span><span class="token punctuation markdown">,</span>
    <span class="token property markdown">"previewUrl"</span><span class="token operator markdown">:</span> <span class="token string markdown">"https://ams.cube.cndinfo.com/onlinePreview/secure?token=jrjDSjmaETsS5MsSS10MmL2ZfhaKWMfb"</span>
  <span class="token punctuation markdown">}</span>
<span class="token punctuation markdown">}</span>
</code></pre>
</div><h3 id="实战" class="markdown"><a class="header-anchor markdown" href="#实战">#</a> 实战</h3>
<p class="markdown">Web 端无需创建 <code class="markdown">web-view</code> 页面，取到预览地址后直接打开即可。</p>
<p class="markdown">App 端需要通过新增一个 webview 页面来显示预览。</p>
<h4 id="预览线上或本地文件" class="markdown"><a class="header-anchor markdown" href="#预览线上或本地文件">#</a> 预览线上或本地文件</h4>
<p class="markdown">我们按照创建、预览线上文件、预览本地文件三个步骤来介绍。</p>
<h5 id="创建-web-view-预览页面" class="markdown"><a class="header-anchor markdown" href="#创建-web-view-预览页面">#</a> 创建 web-view 预览页面</h5>
<p class="markdown">首先新建一个预览页，专门用于显示文件预览。</p>
<div class="language-html markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token comment markdown">&lt;!-- pages/preview/index --&gt;</span>
<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>template</span><span class="token punctuation markdown">&gt;</span></span>
  <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>view</span> <span class="token attr-name markdown">class</span><span class="token attr-value markdown"><span class="token punctuation attr-equals markdown">=</span><span class="token punctuation markdown">"</span>web-view<span class="token punctuation markdown">"</span></span><span class="token punctuation markdown">&gt;</span></span>
    <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>web-view</span> <span class="token attr-name markdown">:src</span><span class="token attr-value markdown"><span class="token punctuation attr-equals markdown">=</span><span class="token punctuation markdown">"</span>previewUrl<span class="token punctuation markdown">"</span></span> <span class="token punctuation markdown">/&gt;</span></span>
  <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>view</span><span class="token punctuation markdown">&gt;</span></span>
<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>template</span><span class="token punctuation markdown">&gt;</span></span>
<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>script</span><span class="token punctuation markdown">&gt;</span></span><span class="token script markdown"><span class="token language-javascript markdown">
  <span class="token keyword markdown">import</span> <span class="token punctuation markdown">{</span> base64decode <span class="token punctuation markdown">}</span> <span class="token keyword markdown">from</span> <span class="token string markdown">"@cndinfo/cube-ams-sdk/src/utils"</span><span class="token punctuation markdown">;</span>
  <span class="token keyword markdown">export</span> <span class="token keyword markdown">default</span> <span class="token punctuation markdown">{</span>
    <span class="token function markdown">data</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      <span class="token keyword markdown">return</span> <span class="token punctuation markdown">{</span>
        <span class="token literal-property property markdown">previewUrl</span><span class="token operator markdown">:</span> <span class="token string markdown">""</span><span class="token punctuation markdown">,</span>
      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">;</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
    <span class="token function markdown">onLoad</span><span class="token punctuation markdown">(</span><span class="token parameter markdown">data</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      <span class="token keyword markdown">if</span> <span class="token punctuation markdown">(</span>data <span class="token operator markdown">&amp;&amp;</span> data<span class="token punctuation markdown">.</span>preview<span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
        <span class="token comment markdown">// 传给 webview 的 url 建议是先 base64 加密</span>
        <span class="token comment markdown">// 展示时需要先解密</span>
        <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>previewUrl <span class="token operator markdown">=</span> <span class="token function markdown">base64decode</span><span class="token punctuation markdown">(</span>data<span class="token punctuation markdown">.</span>preview<span class="token punctuation markdown">)</span><span class="token punctuation markdown">;</span>
      <span class="token punctuation markdown">}</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
    <span class="token literal-property property markdown">methods</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span><span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
  <span class="token punctuation markdown">}</span><span class="token punctuation markdown">;</span>
</span></span><span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>script</span><span class="token punctuation markdown">&gt;</span></span>
</code></pre>
</div><h5 id="预览线上文件" class="markdown"><a class="header-anchor markdown" href="#预览线上文件">#</a> 预览线上文件</h5>
<p class="markdown">预览线上文件只需要将文件地址直接传给 ams 实例。</p>
<div class="language-vue markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>template</span><span class="token punctuation markdown">&gt;</span></span>
  <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>view</span><span class="token punctuation markdown">&gt;</span></span>
    <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>button</span> <span class="token attr-name markdown">@click</span><span class="token attr-value markdown"><span class="token punctuation attr-equals markdown">=</span><span class="token punctuation markdown">"</span>handlePreview<span class="token punctuation markdown">"</span></span><span class="token punctuation markdown">&gt;</span></span>预览<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>button</span><span class="token punctuation markdown">&gt;</span></span>
  <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>view</span><span class="token punctuation markdown">&gt;</span></span>
<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>template</span><span class="token punctuation markdown">&gt;</span></span>

<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>script</span><span class="token punctuation markdown">&gt;</span></span><span class="token script markdown"><span class="token language-javascript markdown">
<span class="token comment markdown">// 导入 sdk 实例</span>
<span class="token keyword markdown">import</span> <span class="token punctuation markdown">{</span> amsClient <span class="token punctuation markdown">}</span> <span class="token keyword markdown">from</span> <span class="token string markdown">"@/common/utils/ams"</span><span class="token punctuation markdown">;</span>
<span class="token keyword markdown">import</span> <span class="token punctuation markdown">{</span> base64encode <span class="token punctuation markdown">}</span> <span class="token keyword markdown">from</span> <span class="token string markdown">"@cndinfo/cube-ams-sdk/src/utils"</span><span class="token punctuation markdown">;</span>

<span class="token keyword markdown">export</span> <span class="token keyword markdown">default</span> <span class="token punctuation markdown">{</span>
  <span class="token literal-property property markdown">methods</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
    <span class="token keyword markdown">async</span> <span class="token function markdown">handlePreview</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      <span class="token keyword markdown">const</span> params <span class="token operator markdown">=</span> <span class="token punctuation markdown">{</span>
        <span class="token comment markdown">// 原始文件地址</span>
        <span class="token literal-property property markdown">previewUrl</span><span class="token operator markdown">:</span>
          <span class="token string markdown">"https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"</span><span class="token punctuation markdown">,</span>
        <span class="token comment markdown">// 水印文字</span>
        <span class="token literal-property property markdown">watermarkText</span><span class="token operator markdown">:</span> <span class="token string markdown">"文字水印"</span><span class="token punctuation markdown">,</span>
      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">;</span>
      <span class="token keyword markdown">const</span> <span class="token punctuation markdown">{</span> data <span class="token punctuation markdown">}</span> <span class="token operator markdown">=</span> <span class="token keyword markdown">await</span> amsClient<span class="token punctuation markdown">.</span><span class="token function markdown">previewFileByLink</span><span class="token punctuation markdown">(</span>params<span class="token punctuation markdown">)</span><span class="token punctuation markdown">;</span>

      <span class="token comment markdown">// 跳转到上述步骤中创建的预览页</span>
      <span class="token comment markdown">// URL 可能包含特殊字符，建议经过 base64 加密</span>
      uni<span class="token punctuation markdown">.</span><span class="token function markdown">navigateTo</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
        <span class="token literal-property property markdown">url</span><span class="token operator markdown">:</span> <span class="token template-string markdown"><span class="token template-punctuation string markdown">`</span><span class="token string markdown">/pages/preview/index?previewUrl=</span><span class="token interpolation markdown"><span class="token interpolation-punctuation punctuation markdown">${</span><span class="token function markdown">base64encode</span><span class="token punctuation markdown">(</span>data<span class="token punctuation markdown">.</span>previewUrl<span class="token punctuation markdown">)</span><span class="token interpolation-punctuation punctuation markdown">}</span></span><span class="token template-punctuation string markdown">`</span></span><span class="token punctuation markdown">,</span>
      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span><span class="token punctuation markdown">;</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
  <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
<span class="token punctuation markdown">}</span><span class="token punctuation markdown">;</span>
</span></span><span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>script</span><span class="token punctuation markdown">&gt;</span></span>
</code></pre>
</div><h5 id="预览本地文件" class="markdown"><a class="header-anchor markdown" href="#预览本地文件">#</a> 预览本地文件</h5>
<p class="markdown">预览本地文件需要将本地文件的 blob 地址传给 ams 实例。</p>
<div class="language-vue markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>template</span><span class="token punctuation markdown">&gt;</span></span>
  <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>view</span><span class="token punctuation markdown">&gt;</span></span>
    <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>button</span> <span class="token attr-name markdown">@click</span><span class="token attr-value markdown"><span class="token punctuation attr-equals markdown">=</span><span class="token punctuation markdown">"</span>handlePreview<span class="token punctuation markdown">"</span></span><span class="token punctuation markdown">&gt;</span></span>预览<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>button</span><span class="token punctuation markdown">&gt;</span></span>
  <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>view</span><span class="token punctuation markdown">&gt;</span></span>
<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>template</span><span class="token punctuation markdown">&gt;</span></span>

<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>script</span><span class="token punctuation markdown">&gt;</span></span><span class="token script markdown"><span class="token language-javascript markdown">
<span class="token keyword markdown">import</span> <span class="token punctuation markdown">{</span> amsClient <span class="token punctuation markdown">}</span> <span class="token keyword markdown">from</span> <span class="token string markdown">"@/common/utils/ams"</span><span class="token punctuation markdown">;</span>
<span class="token keyword markdown">import</span> <span class="token punctuation markdown">{</span> base64encode <span class="token punctuation markdown">}</span> <span class="token keyword markdown">from</span> <span class="token string markdown">"@cndinfo/cube-ams-sdk/src/utils"</span><span class="token punctuation markdown">;</span>

<span class="token keyword markdown">export</span> <span class="token keyword markdown">default</span> <span class="token punctuation markdown">{</span>
  <span class="token literal-property property markdown">methods</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
    <span class="token keyword markdown">async</span> <span class="token function markdown">handlePreview</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      uni<span class="token punctuation markdown">.</span><span class="token function markdown">chooseImage</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
        <span class="token literal-property property markdown">sourceType</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">[</span><span class="token string markdown">"album"</span><span class="token punctuation markdown">]</span><span class="token punctuation markdown">,</span>
        <span class="token function-variable function markdown">success</span><span class="token operator markdown">:</span> <span class="token keyword markdown">async</span> <span class="token punctuation markdown">(</span><span class="token parameter markdown">e</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
          <span class="token keyword markdown">const</span> tempFilePaths <span class="token operator markdown">=</span> e<span class="token punctuation markdown">.</span>tempFilePaths <span class="token operator markdown">||</span> <span class="token punctuation markdown">[</span><span class="token punctuation markdown">]</span><span class="token punctuation markdown">;</span>
          <span class="token keyword markdown">const</span> params <span class="token operator markdown">=</span> <span class="token punctuation markdown">{</span>
            <span class="token comment markdown">// 本地文件</span>
            <span class="token literal-property property markdown">filePath</span><span class="token operator markdown">:</span> tempFilePaths<span class="token punctuation markdown">[</span><span class="token number markdown">0</span><span class="token punctuation markdown">]</span><span class="token punctuation markdown">,</span>
            <span class="token comment markdown">// 有效期（分钟）</span>
            <span class="token literal-property property markdown">expireTime</span><span class="token operator markdown">:</span> <span class="token number markdown">30</span><span class="token punctuation markdown">,</span>
            <span class="token comment markdown">// 压缩比例</span>
            <span class="token literal-property property markdown">compress</span><span class="token operator markdown">:</span> <span class="token number markdown">0.8</span><span class="token punctuation markdown">,</span>
            <span class="token comment markdown">// 水印文字</span>
            <span class="token literal-property property markdown">watermarkText</span><span class="token operator markdown">:</span> <span class="token string markdown">"文字水印"</span><span class="token punctuation markdown">,</span>
          <span class="token punctuation markdown">}</span><span class="token punctuation markdown">;</span>
          <span class="token keyword markdown">const</span> <span class="token punctuation markdown">{</span> data <span class="token punctuation markdown">}</span> <span class="token operator markdown">=</span> <span class="token keyword markdown">await</span> amsClient<span class="token punctuation markdown">.</span><span class="token function markdown">previewFileByUpload</span><span class="token punctuation markdown">(</span>params<span class="token punctuation markdown">)</span><span class="token punctuation markdown">;</span>

          <span class="token comment markdown">// 跳转到上述步骤中创建的预览页</span>
          <span class="token comment markdown">// URL 可能包含特殊字符，建议经过 base64 加密</span>
          uni<span class="token punctuation markdown">.</span><span class="token function markdown">navigateTo</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
            <span class="token literal-property property markdown">url</span><span class="token operator markdown">:</span> <span class="token template-string markdown"><span class="token template-punctuation string markdown">`</span><span class="token string markdown">/pages/preview/index?previewUrl=</span><span class="token interpolation markdown"><span class="token interpolation-punctuation punctuation markdown">${</span><span class="token function markdown">base64encode</span><span class="token punctuation markdown">(</span>
              data<span class="token punctuation markdown">.</span>previewUrl
            <span class="token punctuation markdown">)</span><span class="token interpolation-punctuation punctuation markdown">}</span></span><span class="token template-punctuation string markdown">`</span></span><span class="token punctuation markdown">,</span>
          <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span><span class="token punctuation markdown">;</span>
        <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span><span class="token punctuation markdown">;</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
  <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
<span class="token punctuation markdown">}</span><span class="token punctuation markdown">;</span>
</span></span><span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>script</span><span class="token punctuation markdown">&gt;</span></span>
</code></pre>
</div><h4 id="app-预览且支持下载" class="markdown"><a class="header-anchor markdown" href="#app-预览且支持下载">#</a> App 预览且支持下载</h4>
<p class="markdown">如果只是需要预览，用上面的方法即可，更为简单。</p>
<p class="markdown">但如果在 App 内还想要预览的同时支持用户下载原始文件，可以换成使用 <code class="markdown">plus.webview.create</code> 方法来创建 webview，这种方式可以在导航栏自定义下载按钮。</p>
<p class="markdown">下载可以有两种方式，一种是跳转到外部手机浏览器进行下载。<br class="markdown">
另一种是直接下载到本地（默认地址是 App 内的缓存路径，如果需要永久化存储，需要使用 <a href="https://zh.uniapp.dcloud.io/api/file/file.html" class="markdown">uni.saveFile</a> 方法）。如果下载后需要打开，可以使用 <a href="https://uniapp.dcloud.net.cn/api/file/file.html#opendocument" class="markdown">uni.openDocument</a> 方法。</p>
<div class="language-html markdown"><pre v-pre="" class="markdown"><code class="markdown"><span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>template</span><span class="token punctuation markdown">&gt;</span></span>
  <span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>view</span> <span class="token attr-name markdown">class</span><span class="token attr-value markdown"><span class="token punctuation attr-equals markdown">=</span><span class="token punctuation markdown">"</span>web-view<span class="token punctuation markdown">"</span></span> <span class="token punctuation markdown">/&gt;</span></span>
<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>template</span><span class="token punctuation markdown">&gt;</span></span>
<span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;</span>script</span><span class="token punctuation markdown">&gt;</span></span><span class="token script markdown"><span class="token language-javascript markdown">
  <span class="token keyword markdown">import</span> <span class="token punctuation markdown">{</span> base64decode <span class="token punctuation markdown">}</span> <span class="token keyword markdown">from</span> <span class="token string markdown">"@cndinfo/cube-ams-sdk/src/utils"</span>
  <span class="token keyword markdown">export</span> <span class="token keyword markdown">default</span> <span class="token punctuation markdown">{</span>
    <span class="token function markdown">data</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      <span class="token keyword markdown">return</span> <span class="token punctuation markdown">{</span>
        <span class="token literal-property property markdown">previewUrl</span><span class="token operator markdown">:</span> <span class="token string markdown">""</span><span class="token punctuation markdown">,</span>
        <span class="token literal-property property markdown">webview</span><span class="token operator markdown">:</span> <span class="token keyword markdown">null</span>
      <span class="token punctuation markdown">}</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
    <span class="token function markdown">onLoad</span><span class="token punctuation markdown">(</span><span class="token parameter markdown">data</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      <span class="token keyword markdown">if</span> <span class="token punctuation markdown">(</span>data <span class="token operator markdown">&amp;&amp;</span> data<span class="token punctuation markdown">.</span>preview<span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
        <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>previewUrl <span class="token operator markdown">=</span> <span class="token function markdown">base64decode</span><span class="token punctuation markdown">(</span>data<span class="token punctuation markdown">.</span>preview<span class="token punctuation markdown">)</span>
      <span class="token punctuation markdown">}</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
    <span class="token function markdown">onReady</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      <span class="token function markdown">setTimeout</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
        <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span><span class="token function markdown">create</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span>
      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span> <span class="token number markdown">500</span><span class="token punctuation markdown">)</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
    <span class="token function markdown">onBackPress</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
      <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>webview<span class="token punctuation markdown">.</span><span class="token function markdown">close</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span>
    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
    <span class="token literal-property property markdown">methods</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
      <span class="token function markdown">create</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
        <span class="token keyword markdown">const</span> loading <span class="token operator markdown">=</span> plus<span class="token punctuation markdown">.</span>nativeUI<span class="token punctuation markdown">.</span><span class="token function markdown">showWaiting</span><span class="token punctuation markdown">(</span><span class="token string markdown">"加载中，请稍候..."</span><span class="token punctuation markdown">)</span>
        <span class="token comment markdown">// 创建一个新的webview窗口</span>
        <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>webview <span class="token operator markdown">=</span> plus<span class="token punctuation markdown">.</span>webview<span class="token punctuation markdown">.</span><span class="token function markdown">create</span><span class="token punctuation markdown">(</span><span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>previewUrl<span class="token punctuation markdown">,</span> <span class="token string markdown">"_previewUrl"</span><span class="token punctuation markdown">,</span> <span class="token punctuation markdown">{</span>
          <span class="token literal-property property markdown">scalable</span><span class="token operator markdown">:</span> <span class="token boolean markdown">true</span><span class="token punctuation markdown">,</span>
          <span class="token literal-property property markdown">top</span><span class="token operator markdown">:</span> <span class="token string markdown">"0px"</span><span class="token punctuation markdown">,</span>
          <span class="token literal-property property markdown">bottom</span><span class="token operator markdown">:</span> <span class="token string markdown">"0px"</span><span class="token punctuation markdown">,</span>
          <span class="token literal-property property markdown">left</span><span class="token operator markdown">:</span> <span class="token string markdown">"0px"</span><span class="token punctuation markdown">,</span>
          <span class="token literal-property property markdown">right</span><span class="token operator markdown">:</span> <span class="token string markdown">"0px"</span><span class="token punctuation markdown">,</span>
          <span class="token literal-property property markdown">titleNView</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
            <span class="token literal-property property markdown">autoBackButton</span><span class="token operator markdown">:</span> <span class="token boolean markdown">true</span><span class="token punctuation markdown">,</span>
            <span class="token literal-property property markdown">style</span><span class="token operator markdown">:</span> <span class="token string markdown">"transparent"</span><span class="token punctuation markdown">,</span>
            <span class="token literal-property property markdown">backgroundColor</span><span class="token operator markdown">:</span> <span class="token string markdown">"#fff"</span><span class="token punctuation markdown">,</span>
            <span class="token literal-property property markdown">titleText</span><span class="token operator markdown">:</span> <span class="token string markdown">"预览"</span><span class="token punctuation markdown">,</span>
            <span class="token literal-property property markdown">buttons</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">[</span>
              <span class="token punctuation markdown">{</span>
                <span class="token literal-property property markdown">text</span><span class="token operator markdown">:</span> <span class="token string markdown">"应用内下载"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">float</span><span class="token operator markdown">:</span> <span class="token string markdown">"right"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">fontSize</span><span class="token operator markdown">:</span> <span class="token string markdown">"14px"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">color</span><span class="token operator markdown">:</span> <span class="token string markdown">"#1259E5"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">width</span><span class="token operator markdown">:</span> <span class="token string markdown">"90px"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">onclick</span><span class="token operator markdown">:</span> <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>downLoad
              <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
              <span class="token punctuation markdown">{</span>
                <span class="token literal-property property markdown">text</span><span class="token operator markdown">:</span> <span class="token string markdown">"跳转下载"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">float</span><span class="token operator markdown">:</span> <span class="token string markdown">"right"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">fontSize</span><span class="token operator markdown">:</span> <span class="token string markdown">"14px"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">color</span><span class="token operator markdown">:</span> <span class="token string markdown">"#1259E5"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">width</span><span class="token operator markdown">:</span> <span class="token string markdown">"70px"</span><span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">onclick</span><span class="token operator markdown">:</span> <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>openUrl
              <span class="token punctuation markdown">}</span>
            <span class="token punctuation markdown">]</span>
          <span class="token punctuation markdown">}</span>
        <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>

        <span class="token comment markdown">// 监听加载完成事件</span>
        <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>webview<span class="token punctuation markdown">.</span><span class="token function markdown">addEventListener</span><span class="token punctuation markdown">(</span><span class="token string markdown">"loaded"</span><span class="token punctuation markdown">,</span> <span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
          loading<span class="token punctuation markdown">.</span><span class="token function markdown">close</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span>
          <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>webview<span class="token punctuation markdown">.</span><span class="token function markdown">show</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span>
        <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>

      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
      <span class="token function markdown">openUrl</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
        <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span><span class="token function markdown">downLoad</span><span class="token punctuation markdown">(</span><span class="token boolean markdown">false</span><span class="token punctuation markdown">)</span>
      <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
      <span class="token function markdown">downLoad</span><span class="token punctuation markdown">(</span><span class="token parameter markdown">flag</span><span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
        <span class="token keyword markdown">const</span> url <span class="token operator markdown">=</span> <span class="token keyword markdown">this</span><span class="token punctuation markdown">.</span>previewUrl<span class="token punctuation markdown">.</span><span class="token function markdown">replace</span><span class="token punctuation markdown">(</span>
          <span class="token regex markdown"><span class="token regex-delimiter markdown">/</span><span class="token regex-source language-regex markdown">\/onlinePreview(\/secure)?</span><span class="token regex-delimiter markdown">/</span></span><span class="token punctuation markdown">,</span>
          <span class="token template-string markdown"><span class="token template-punctuation string markdown">`</span><span class="token string markdown">/api/preview$1/onlinePreview</span><span class="token template-punctuation string markdown">`</span></span>
        <span class="token punctuation markdown">)</span>
        console<span class="token punctuation markdown">.</span><span class="token function markdown">log</span><span class="token punctuation markdown">(</span><span class="token string markdown">"url"</span><span class="token punctuation markdown">,</span> url<span class="token punctuation markdown">)</span>
        uni<span class="token punctuation markdown">.</span><span class="token function markdown">request</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
          url<span class="token punctuation markdown">,</span>
          <span class="token literal-property property markdown">header</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span>
            <span class="token string-property property markdown">"content-type"</span><span class="token operator markdown">:</span> <span class="token string markdown">"application/json"</span>
          <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
          <span class="token function-variable function markdown">success</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">(</span><span class="token parameter markdown">res</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
            <span class="token keyword markdown">if</span> <span class="token punctuation markdown">(</span>res <span class="token operator markdown">&amp;&amp;</span> res<span class="token punctuation markdown">.</span>data <span class="token operator markdown">&amp;&amp;</span> res<span class="token punctuation markdown">.</span>data<span class="token punctuation markdown">.</span>data <span class="token operator markdown">&amp;&amp;</span> res<span class="token punctuation markdown">.</span>data<span class="token punctuation markdown">.</span>data<span class="token punctuation markdown">.</span>file<span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
              <span class="token keyword markdown">const</span> fileUrl <span class="token operator markdown">=</span> res<span class="token punctuation markdown">.</span>data<span class="token punctuation markdown">.</span>data<span class="token punctuation markdown">.</span>file<span class="token punctuation markdown">.</span>url <span class="token comment markdown">// 待下载文件的url</span>
              <span class="token comment markdown">// 直接下载到本地</span>
              <span class="token keyword markdown">if</span> <span class="token punctuation markdown">(</span>flag<span class="token punctuation markdown">)</span> <span class="token punctuation markdown">{</span>
                uni<span class="token punctuation markdown">.</span><span class="token function markdown">downloadFile</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
                  <span class="token literal-property property markdown">url</span><span class="token operator markdown">:</span> fileUrl<span class="token punctuation markdown">,</span>
                  <span class="token literal-property property markdown">header</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">{</span><span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
                  <span class="token function-variable function markdown">success</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">(</span><span class="token parameter markdown">downRes</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
                    uni<span class="token punctuation markdown">.</span><span class="token function markdown">showToast</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
                      <span class="token literal-property property markdown">title</span><span class="token operator markdown">:</span> <span class="token string markdown">"下载成功"</span><span class="token punctuation markdown">,</span>
                      <span class="token literal-property property markdown">icon</span><span class="token operator markdown">:</span> <span class="token string markdown">"none"</span>
                    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>
                    <span class="token comment markdown">// 下载后打开（非必须）</span>
                    uni<span class="token punctuation markdown">.</span><span class="token function markdown">openDocument</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
                      <span class="token literal-property property markdown">filePath</span><span class="token operator markdown">:</span> downRes<span class="token punctuation markdown">.</span>tempFilePath<span class="token punctuation markdown">,</span>
                      <span class="token literal-property property markdown">showMenu</span><span class="token operator markdown">:</span> <span class="token boolean markdown">true</span><span class="token punctuation markdown">,</span>
                      <span class="token function-variable function markdown">success</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">(</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
                        console<span class="token punctuation markdown">.</span><span class="token function markdown">log</span><span class="token punctuation markdown">(</span><span class="token string markdown">"打开文档成功"</span><span class="token punctuation markdown">)</span>
                      <span class="token punctuation markdown">}</span>
                    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>
                  <span class="token punctuation markdown">}</span>
                  <span class="token function-variable function markdown">fail</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">(</span><span class="token parameter markdown">downErr</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
                    uni<span class="token punctuation markdown">.</span><span class="token function markdown">showToast</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
                      <span class="token literal-property property markdown">title</span><span class="token operator markdown">:</span> downErr<span class="token punctuation markdown">.</span>message<span class="token punctuation markdown">,</span>
                      <span class="token literal-property property markdown">icon</span><span class="token operator markdown">:</span> <span class="token string markdown">"none"</span>
                    <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>
                  <span class="token punctuation markdown">}</span>
                <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>
              <span class="token punctuation markdown">}</span> <span class="token keyword markdown">else</span> <span class="token punctuation markdown">{</span>
                <span class="token comment markdown">// 外部浏览器下载</span>
                plus<span class="token punctuation markdown">.</span>runtime<span class="token punctuation markdown">.</span><span class="token function markdown">openURL</span><span class="token punctuation markdown">(</span>fileUrl<span class="token punctuation markdown">)</span>
              <span class="token punctuation markdown">}</span>
            <span class="token punctuation markdown">}</span> <span class="token keyword markdown">else</span> <span class="token punctuation markdown">{</span>
              uni<span class="token punctuation markdown">.</span><span class="token function markdown">showToast</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
                <span class="token literal-property property markdown">title</span><span class="token operator markdown">:</span> res<span class="token punctuation markdown">.</span>data<span class="token punctuation markdown">.</span>message<span class="token punctuation markdown">,</span>
                <span class="token literal-property property markdown">icon</span><span class="token operator markdown">:</span> <span class="token string markdown">"none"</span>
              <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>
            <span class="token punctuation markdown">}</span>
          <span class="token punctuation markdown">}</span><span class="token punctuation markdown">,</span>
          <span class="token function-variable function markdown">fail</span><span class="token operator markdown">:</span> <span class="token punctuation markdown">(</span><span class="token parameter markdown">err</span><span class="token punctuation markdown">)</span> <span class="token operator markdown">=&gt;</span> <span class="token punctuation markdown">{</span>
            uni<span class="token punctuation markdown">.</span><span class="token function markdown">showToast</span><span class="token punctuation markdown">(</span><span class="token punctuation markdown">{</span>
              <span class="token literal-property property markdown">title</span><span class="token operator markdown">:</span> err<span class="token punctuation markdown">.</span>message<span class="token punctuation markdown">,</span>
              <span class="token literal-property property markdown">icon</span><span class="token operator markdown">:</span> <span class="token string markdown">"none"</span>
            <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>
          <span class="token punctuation markdown">}</span>
        <span class="token punctuation markdown">}</span><span class="token punctuation markdown">)</span>
      <span class="token punctuation markdown">}</span>
    <span class="token punctuation markdown">}</span>
  <span class="token punctuation markdown">}</span>
</span></span><span class="token tag markdown"><span class="token tag markdown"><span class="token punctuation markdown">&lt;/</span>script</span><span class="token punctuation markdown">&gt;</span></span>
</code></pre>
</div><h4 id="移动端预览效果" class="markdown"><a class="header-anchor markdown" href="#移动端预览效果">#</a> 移动端预览效果</h4>
<p class="markdown">移动端预览以及下载效果可以看下面的视频。</p>
<video id="video" controls="" preload="none" width="300px" class="markdown">
  <source id="mp4" src="https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview-mobile-demo.mp4" type="video/mp4" class="markdown">
</video>
</body>
      </section>
    </template>
    <script>
    import CryptoJS from "crypto-js/crypto-js";
import axios from "axios";
      export default {
        name: 'component-doc',
        components: {
          "element-demo0": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('div',[_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"240px","font-size":"16px"}},[_vm._v("是否显示所有格式（总"+_vm._s(_vm.fileList.length)+"种）")]),_vm._v(" "),_c('el-switch',{model:{value:(_vm.showAll),callback:function ($$v) {_vm.showAll=$$v},expression:"showAll"}})],1),_vm._v(" "),_c('el-table',{attrs:{"data":_vm.tableData,"span-method":_vm.objectSpanMethod,"cell-style":_vm.addCellBorder,"border":""}},[_c('el-table-column',{attrs:{"prop":"id","label":"文件分类","width":"150"}}),_vm._v(" "),_c('el-table-column',{attrs:{"prop":"type","label":"文件格式","width":"100"}}),_vm._v(" "),_c('el-table-column',{attrs:{"prop":"desc","label":"说明"}}),_vm._v(" "),_c('el-table-column',{attrs:{"prop":"size","label":"文件大小","width":"100"}}),_vm._v(" "),_c('el-table-column',{attrs:{"label":"操作","width":"80"},scopedSlots:_vm._u([{key:"default",fn:function(scope){return [_c('cube-button',{attrs:{"type":"text","size":"small"},nativeOn:{"click":function($event){$event.preventDefault();return _vm.gotoPreview(scope.$index, _vm.tableData)}}},[_vm._v("\n            点击预览\n          ")])]}}])})],1)],1)]],2)}
var staticRenderFns = []

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const democomponentExport = {
    data() {
      return {
        showAll: false,
        // prettier-ignore
        fileList: [
          ["通用文档", ".pdf", "PDF 文件", "128K", "preview.pdf"],
          ["MS Office 文档", ".doc", "Word 97-2003 文件", "60K", "preview.doc"],
          ["MS Office 文档", ".docx", "Word 文件", "17K", "preview.docx"],
          ["MS Office 文档", ".dot", "Word 97-2003 模板文件", "112K", "preview.dot", 1],
          ["MS Office 文档", ".dotm", "Word 模板文件", "26K", "preview.dotm", 1],
          ["MS Office 文档", ".dotx", "Word 带宏的模板文件", "26K", "preview.dotx", 1],
          ["MS Office 文档", ".xls", "Excel 97-2003文件", "42K", "preview.xls"],
          ["MS Office 文档", ".xlsx", "Excel 文件", "24K", "preview.xlsx"],
          ["MS Office 文档", ".xla", "Excel 97-2003 加载宏文件", "34K", "preview.xla", 1],
          // ["MS Office 文档", ".xlam", "Excel 加载宏文件", "22K", "preview.xlam", 1],
          ["MS Office 文档", ".xlt", "Excel 97-2003 模板文件", "34K", "preview.xlt", 1],
          ["MS Office 文档", ".xltm", "Excel 启用宏的模板", "22K", "preview.xltm", 1],
          ["MS Office 文档", ".ppt", "PPT 97-2003 文件", "1.1M", "preview.ppt"],
          ["MS Office 文档", ".pptx", "PPT 文件", "187K", "preview.pptx"],

          ["WPS 文档", ".wps", "WPS文字 文件", "112K", "preview.wps"],
          ["WPS 文档", ".wpt", "WPS文字 模板文件", "112K", "preview.wpt", 1],
          ["WPS 文档", ".et", "WPS表格 文件", "34K", "preview.et"],
          ["WPS 文档", ".ett", "WPS表格 模板文件", "34K", "preview.ett", 1],
          ["WPS 文档", ".dps", "WPS演示 文件", "890K", "preview.dps"],
          ["macOS iWork 文档", ".pages", "macOS Pages 文稿文件", "719K", "preview.pages"],
          // ["macOS iWork 文档", ".numbers", "macOS Numbers 表格文件", "823K", "preview.numbers"],
          // ["macOS iWork 文档", ".key", "macOS Keynote 讲演文件", "1.5K", "preview.key"],

          ["纯文本", ".txt", "文本文件", "28K", "preview.txt"],
          ["纯文本", ".md", "Markdown 文件", "28K", "preview.md"],
          ["邮件", ".eml", "EML 文件", "28K", "preview.eml"],
          ["图片", ".png", "PNG 文件", "8K", "preview.png"],
          ["图片", ".jpg", "JPG 文件", "403K", "preview.jpg"],
          ["图片", ".jpeg", "JPEG 文件", "403K", "preview.jpeg", 1],
          ["图片", ".svg", "SVG 矢量文件", "56K", "preview.svg"],
          ["图片", ".jfif", "JFIF 文件", "419K", "preview.jfif"],
          ["图片", ".webp", "WEBP 文件", "284K", "preview.webp"],
          ["图片", ".bmp", "BMP 文件", "1M", "preview.bmp", 1],
          ["图片", ".gif", "GIF 文件", "130K", "preview.gif"],
          // ["图片", ".tiff", "TIF 文件", "940K", "preview.tiff"], // 待修复
          ["音频", ".mp3", "MP3 文件", "929K", "preview.mp3"],
          ["音频", ".flac", "FLAC 无损音频文件", "4.9M", "preview.flac"],
          // ["音频", ".ape", "APE 无损音频文件", "929K", "preview.ape"], // 不支持
          ["音频", ".m4a", "Apple 无损音频文件", "983K", "preview.m4a", 1],
          ["音频", ".wav", "Windows 无损音频文件", "10.2M", "preview.wav", 1],
          ["视频", ".mp4", "MP4 文件", "14.8M", "preview.mp4"],
          ["视频", ".mov", "MOV 文件", "5.4M", "preview.mov"],
          // ["视频", ".mkv", "MKV 文件", "5.3M", "preview.mkv"],
          // ["视频", ".mpg", "MPG 文件", "41M", "preview.mpg"],
          // ["视频", ".wmv", "WMV 文件", "40.8M", "preview.wmv"],
          // ["视频", ".rm", "RM 文件", "923K", "preview.rm"],
          // ["视频", ".rmvb", "RMVB 文件", "943K", "preview.rmvb"],
          ["压缩包", ".zip", "ZIP 压缩文件", "16.5M", "preview.zip"],
          ["压缩包", ".zip", "ZIP 压缩文件（内部嵌套 RAR）", "14.9M", "preview.rar.zip", 1],
          ["压缩包", ".rar", "RAR 压缩文件", "14.9M", "preview.rar"],
          ["压缩包", ".rar", "RAR 压缩文件（内部嵌套 ZIP）", "16.5M", "preview.zip.rar", 1],
          ["压缩包", ".7z", "7z 压缩文件", "14.9M", "preview.7z"],
        ],
      };
    },
    computed: {
      tableData() {
        return this.fileList
          .map((x) => ({
            id: x[0],
            type: x[1],
            desc: x[2],
            size: x[3],
            name: x[4],
            hide: x[5],
          }))
          .filter((x) => (this.showAll ? true : !x.hide));
      },
    },
    methods: {
      gotoPreview(index, data) {
        console.log("preview", data[index]);
        const url = base64encode(
          `https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/${data[index].name}`
        );
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        window.open(`${previewDomain}/onlinePreview/?url=${url}`, "_blank");

        // 调试用
        // const options = {
        //   // hiddenDownload: true,
        //   // hiddenPrint: true,
        //   hiddenConvert: true,
        //   xlsxReadOnly: true,
        // };
        // const optionsEncode = encodeURIComponent(
        //   Object.keys(options)
        //     .reduce((p, c) => {
        //       return p + `${c}=${base64encode(options[c])}&`;
        //     }, "")
        //     .replace(/&$/, "")
        // );
        // // console.log("optionsEncode", optionsEncode);
        // window.open(
        //   `${previewDomain}/onlinePreview/?url=${url}&options=${optionsEncode}`,
        //   "_blank"
        // );
      },
      addCellBorder(event) {
        // console.log("addCellBorder", event);
        // if (event.columnIndex === 0) {
        return {
          borderBottom: "1px solid #EBEEF5",
        };
        // }
      },
      objectSpanMethod({ row, column, rowIndex, columnIndex }) {
        if (columnIndex === 0) {
          const _row = this.flitterData(this.tableData).one[rowIndex];
          const _col = _row > 0 ? 1 : 0;
          return {
            rowspan: _row,
            colspan: _col,
          };
        }
      },
      flitterData(arr) {
        let spanOneArr = [];
        let concatOne = 0;
        arr.forEach((item, index) => {
          if (index === 0) {
            spanOneArr.push(1);
          } else {
            // name 修改
            if (item.id === arr[index - 1].id) {
              spanOneArr[concatOne] += 1;
              spanOneArr.push(0);
            } else {
              spanOneArr.push(1);
              concatOne = index;
            }
          }
        });
        return {
          one: spanOneArr,
        };
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo1": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('div',[_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"80px","font-size":"16px"}},[_vm._v("原始文件")]),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"请输入原始文件地址"},model:{value:(_vm.url),callback:function ($$v) {_vm.url=$$v},expression:"url"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"80px","font-size":"16px"}},[_vm._v("水印文字")]),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"请输入水印文字"},model:{value:(_vm.watermarkText),callback:function ($$v) {_vm.watermarkText=$$v},expression:"watermarkText"}})],1),_vm._v(" "),_vm._m(0),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px","gap":"16px"}},[_c('el-input',{attrs:{"placeholder":"水印透明度: 0.5"},model:{value:(_vm.watermarkAlpha),callback:function ($$v) {_vm.watermarkAlpha=$$v},expression:"watermarkAlpha"}}),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"水印宽度: 100px"},model:{value:(_vm.watermarkWidth),callback:function ($$v) {_vm.watermarkWidth=$$v},expression:"watermarkWidth"}}),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"水印文字大小: 24px"},model:{value:(_vm.watermarkFontSize),callback:function ($$v) {_vm.watermarkFontSize=$$v},expression:"watermarkFontSize"}}),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"水印文字颜色: #1259E5"},model:{value:(_vm.watermarkColor),callback:function ($$v) {_vm.watermarkColor=$$v},expression:"watermarkColor"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"220px","font-size":"16px"}},[_vm._v("是否显示 PDF 下载按钮")]),_vm._v(" "),_c('el-switch',{model:{value:(_vm.hiddenDownload),callback:function ($$v) {_vm.hiddenDownload=$$v},expression:"hiddenDownload"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"220px","font-size":"16px"}},[_vm._v("是否显示 PDF 打印按钮")]),_vm._v(" "),_c('el-switch',{model:{value:(_vm.hiddenPrint),callback:function ($$v) {_vm.hiddenPrint=$$v},expression:"hiddenPrint"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"220px","font-size":"16px"}},[_vm._v("是否隐藏 PDF/JPG 切换按钮（仅限 Office 文档类）")]),_vm._v(" "),_c('el-switch',{model:{value:(_vm.hiddenConvert),callback:function ($$v) {_vm.hiddenConvert=$$v},expression:"hiddenConvert"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"220px","font-size":"16px"}},[_vm._v("是否设置 xlsx 为编辑模式")]),_vm._v(" "),_c('el-switch',{model:{value:(_vm.xlsxReadOnly),callback:function ($$v) {_vm.xlsxReadOnly=$$v},expression:"xlsxReadOnly"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('p',{staticStyle:{"min-width":"80px","font-size":"16px"}},[_vm._v("预览地址")]),_vm._v(" "),_c('p',[_c('a',{staticStyle:{"color":"#1259E5","font-size":"14px","word-wrap":"break-word","word-break":"break-all"},attrs:{"target":"_blank","href":_vm.previewUrl}},[_vm._v(_vm._s(_vm.previewUrl))])])]),_vm._v(" "),_c('cube-button',{staticStyle:{"margin":"0 12px 12px 0"},attrs:{"type":"primary"},on:{"click":_vm.gotoPreview}},[_vm._v("直接预览")])],1)]],2)}
var staticRenderFns = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"80px","font-size":"16px"}},[_vm._v("水印样式")])])}]

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const democomponentExport = {
    data() {
      return {
        url: "",
        watermarkText: "",
        watermarkAlpha: "",
        watermarkFontSize: "",
        watermarkColor: "",
        watermarkWidth: "",
        hiddenDownload: false,
        hiddenPrint: false,
        hiddenConvert: false,
        xlsxReadOnly: false,
      };
    },
    computed: {
      previewUrl() {
        const fileUrl = base64encode(this.url);

        const options = {
          watermarkText: this.watermarkText,
          watermarkAlpha: this.watermarkAlpha,
          watermarkFontSize: this.watermarkFontSize,
          watermarkColor: this.watermarkColor,
          watermarkWidth: this.watermarkWidth,
          hiddenDownload: !this.hiddenDownload,
          hiddenPrint: !this.hiddenPrint,
          hiddenConvert: this.hiddenConvert,
          xlsxReadOnly: !this.xlsxReadOnly,
        };
        // console.log("options", options);
        // const _options = _(options).cloneDeep().value();
        const _options = _.pickBy(
          _.cloneDeep(options),
          (ov) => _.isBoolean(ov) || (_.isString(ov) && ov)
        );
        // console.log("_options", _options);
        // 拼接每个 option，最终进行 encodeURI

        const optionsEncode = encodeURIComponent(
          Object.keys(_options)
            .reduce((p, c) => {
              return p + `${c}=${base64encode(options[c])}&`;
            }, "")
            .replace(/&$/, "")
        );
        console.log("optionsEncode", optionsEncode);
        let result = "";
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        if (this.url && optionsEncode) {
          result = `${previewDomain}/onlinePreview/?url=${fileUrl}&options=${optionsEncode}`;
        } else if (this.url) {
          result = `${previewDomain}/onlinePreview/?url=${fileUrl}`;
        }
        return result;
      },
    },
    methods: {
      gotoPreview() {
        window.open(this.previewUrl, "_blank");
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo2": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('cube-button',{attrs:{"type":"primary"},on:{"click":_vm.gotoPreview}},[_vm._v("点击预览")])]],2)}
var staticRenderFns = []

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkText: "测试水印",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) => {
        return p + `${c}=${base64encode(options[c])}&`;
      }, "")
      .replace(/&$/, "")
  );

  const democomponentExport = {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview() {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = `${previewDomain}/onlinePreview/?url=${fileUrl}&options=${optionsEncode}`;
        window.open(previewUrl, "_blank");
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo3": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('cube-button',{attrs:{"type":"primary"},on:{"click":_vm.gotoPreview}},[_vm._v("点击预览")])]],2)}
var staticRenderFns = []

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkImg:
      "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/file-preview-watermark-image.png",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) => {
        return p + `${c}=${base64encode(options[c])}&`;
      }, "")
      .replace(/&$/, "")
  );

  const democomponentExport = {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview() {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = `${previewDomain}/onlinePreview/?url=${fileUrl}&options=${optionsEncode}`;
        window.open(previewUrl, "_blank");
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo4": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('cube-button',{attrs:{"type":"primary"},on:{"click":_vm.gotoPreview}},[_vm._v("点击预览")])]],2)}
var staticRenderFns = []

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkText: "测试水印",
    title: "自定义标题",
    desc: "自定义描述信息",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) => {
        return p + `${c}=${base64encode(options[c])}&`;
      }, "")
      .replace(/&$/, "")
  );

  const democomponentExport = {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview() {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = `${previewDomain}/onlinePreview/?url=${fileUrl}&options=${optionsEncode}`;
        window.open(previewUrl, "_blank");
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo5": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('cube-button',{attrs:{"type":"primary"},on:{"click":function($event){return _vm.gotoPreview('pdf')}}},[_vm._v("查看PDF格式")]),_vm._v(" "),_c('cube-button',{attrs:{"type":"primary"},on:{"click":function($event){return _vm.gotoPreview('image')}}},[_vm._v("查看图片格式")])]],2)}
var staticRenderFns = []

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const options = {
    watermarkText: "测试水印",
  };
  // 拼接每个 option，最终进行 encodeURI
  const optionsEncode = encodeURIComponent(
    Object.keys(options)
      .reduce((p, c) => {
        return p + `${c}=${base64encode(options[c])}&`;
      }, "")
      .replace(/&$/, "")
  );

  const democomponentExport = {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview(officePreviewType) {
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = (officePreviewType) =>
          `${previewDomain}/onlinePreview/?url=${fileUrl}&options=${optionsEncode}&officePreviewType=${officePreviewType}`;
        window.open(previewUrl(officePreviewType), "_blank");
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo6": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('cube-button',{attrs:{"type":"primary"},on:{"click":function($event){return _vm.gotoPreview('hide')}}},[_vm._v("隐藏下载和打印按钮")]),_vm._v(" "),_c('cube-button',{attrs:{"type":"primary"},on:{"click":function($event){return _vm.gotoPreview()}}},[_vm._v("不隐藏按钮")])]],2)}
var staticRenderFns = []

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const fileUrl = base64encode(
    "https://front-end-huawei-cdn.devops.cndinfo.com/static/docs/fed-doc/preview/preview.pdf"
  );

  const democomponentExport = {
    data() {
      return {
        value: "",
      };
    },
    methods: {
      gotoPreview(officePreviewType) {
        const options = {
          watermarkText: "测试水印",
          hiddenDownload: officePreviewType === "hide" ? true : false,
          hiddenPrint: officePreviewType === "hide" ? true : false,
        };
        // 拼接每个 option，最终进行 encodeURI
        const optionsEncode = encodeURIComponent(
          Object.keys(options)
            .reduce((p, c) => {
              return p + `${c}=${base64encode(options[c])}&`;
            }, "")
            .replace(/&$/, "")
        );
        const previewDomain =
          window.previewDomain || "https://ams.cube.cndinfo.com";
        const previewUrl = (officePreviewType) =>
          `${previewDomain}/onlinePreview/?url=${fileUrl}&options=${optionsEncode}&officePreviewType=${officePreviewType}`;
        window.open(previewUrl(officePreviewType), "_blank");
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),"element-demo7": (function() {
    
    var render = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',[[_c('div',[_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"160px","font-size":"16px"}},[_vm._v("AMS appKey")]),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"请输入 AMS appKey"},model:{value:(_vm.appKey),callback:function ($$v) {_vm.appKey=$$v},expression:"appKey"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"160px","font-size":"16px"}},[_vm._v("AMS appSecret")]),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"请输入 AMS appSecret"},model:{value:(_vm.appSecret),callback:function ($$v) {_vm.appSecret=$$v},expression:"appSecret"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"160px","font-size":"16px"}},[_vm._v("水印文字")]),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"请输入水印文字"},model:{value:(_vm.watermarkText),callback:function ($$v) {_vm.watermarkText=$$v},expression:"watermarkText"}})],1),_vm._v(" "),_c('div',{staticStyle:{"display":"flex","align-items":"center","margin-bottom":"16px"}},[_c('span',{staticStyle:{"width":"160px","font-size":"16px"}},[_vm._v("访问次数限制")]),_vm._v(" "),_c('el-input',{attrs:{"placeholder":"请输入允许预览几次"},model:{value:(_vm.count),callback:function ($$v) {_vm.count=$$v},expression:"count"}})],1),_vm._v(" "),_c('el-upload',{ref:"upload",staticClass:"upload",attrs:{"action":"string","file-list":_vm.fileList,"auto-upload":false,"on-change":_vm.handleChange,"on-preview":_vm.handlePreview,"on-remove":_vm.handleRemove,"multiple":"multiple"}},[_c('cube-button',{attrs:{"slot":"trigger","size":"medium","type":"primary"},on:{"click":_vm.delFile},slot:"trigger"},[_vm._v("1. 选取文件")])],1),_vm._v(" "),_c('cube-button',{staticStyle:{"margin":"12px 12px 12px 0"},attrs:{"size":"medium","type":"primary"},on:{"click":_vm.submitUpload}},[_vm._v("2. 点击预览")])],1)]],2)}
var staticRenderFns = []

  
    const base64encode = (str) =>
    CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(str));

  const democomponentExport = {
    data() {
      return {
        appKey: "",
        appSecret: "",
        watermarkText: "",
        count: "",
        fileList: [],
        multiple: false,
      };
    },
    methods: {
      delFile() {
        console.log("删除文件");
        this.fileList = [];
      },
      handleChange(file, fileList) {
        this.fileList = fileList;
      },
      handleRemove(file, fileList) {
        console.log(file, fileList);
      },
      handlePreview(file) {
        console.log(file);
      },
      submitUpload() {
        let formData = new FormData();
        if (this.fileList[0]) {
          formData.append("previewFile", this.fileList[0].raw);
        }
        if (this.watermarkText) {
          formData.append("watermarkText", this.watermarkText);
        }
        if (this.count) {
          formData.append("count", this.count);
        }

        const previewDomain = "/ams-api"; // "https://ams.cube.cndinfo.com/api"

        const timestamp = Date.now();
        const data = {
          appKey: this.appKey,
          timestamp,
          appSecret: this.appSecret,
          sign: CryptoJS.SHA256(
            this.appKey + timestamp + this.appSecret
          ).toString(),
        };

        // 演示代码通过前端获取 token，实际开发中应该由后端获取
        axios({
          url: previewDomain + "/auth/sign",
          method: "post",
          data,
          headers: {
            "Content-Type": "application/json;chaset=UTF-8",
          },
        })
          .then((res) => {
            console.log("AMS 鉴权: ", res);
            if (res.data && res.data.data && res.data.data.accessToken) {
              axios({
                url: previewDomain + "/preview/secure/file/onlinePreview",
                method: "post",
                data: formData,
                headers: {
                  "Content-Type": "multipart/form-data;charset=utf-8",
                  Authorization: `Bearer ${res.data.data.accessToken}`,
                },
              })
                .then((res2) => {
                  console.log("文件预览: ", res2);
                  if (
                    res2.data &&
                    res2.data.data &&
                    res2.data.data.previewUrl
                  ) {
                    window.open(res2.data.data.previewUrl, "_blank");
                  } else {
                  }
                })
                .catch((err) => {
                  console.log(err);
                });
            } else {
            }
          })
          .catch((err) => {
            console.log(err);
          });
      },
    },
  };
    return {
      render,
      staticRenderFns,
      ...democomponentExport
    }
  })(),
        }
      }
    </script>
  